// Refactored EditorPage - Using extracted components for better maintainability
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  getMyPage, updatePage, togglePublish, addCard, updateCard, deleteCard, reorderCards, requestEmailChange, uploadPhoto,
  adminGetPage, adminUpdatePage, adminTogglePublish, adminAddCard, adminUpdateCard, adminDeleteCard, adminReorderCards, adminUploadPhoto,
  searchPodcasts, searchNewsletters, searchBooks, unfurlLink, adminEnrichFromPdf,
  addAppToPage, updateAppConfig, deleteAppFromPage, getPageSubmissions
} from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Loader2, Trash2, Plus, X, Upload, FileText } from 'lucide-react';
import { toast } from 'sonner';

// Import extracted components
import {
  EditorToolbar,
  HeroSection,
  HorizontalSections,
  InfoCard,
  CardWrapper,
  cssVars,
  CARD_TYPES,
  INVEST_OPTIONS,
  PROFILE_SOCIAL_OPTIONS,
  LINKS_OPTIONS,
  HORIZONTAL_TYPE_MAP,
  DEFAULT_HORIZONTAL_ORDER
} from '../components/editor';
import CardExtras from '../components/editor/CardExtras';

// Import Apps components
import { AppsPanel, AppEditor, PitchMailbox, APP_DEFINITIONS, APP_TYPES } from '../components/apps';
import MeetingAppEditor from '../components/apps/MeetingAppEditor';
import PitchFormBuilder from '../components/apps/PitchFormBuilder';

export default function EditorPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const isAdminMode = !!params.pageId;
  
  console.log('[EditorPage] Initializing, isAdminMode:', isAdminMode, 'pageId:', params.pageId);

  // Core state
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Dialog states
  const [showSettings, setShowSettings] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [editingSocials, setEditingSocials] = useState(false);
  const [editingConnect, setEditingConnect] = useState(false);
  const [editingStats, setEditingStats] = useState(false);
  const [editingHowIInvest, setEditingHowIInvest] = useState(false);
  const [editingInvestmentFocus, setEditingInvestmentFocus] = useState(false);
  const [editingPostInvestment, setEditingPostInvestment] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(false);
  const [editingInvestmentThesis, setEditingInvestmentThesis] = useState(false);
  const [showEnrichDialog, setShowEnrichDialog] = useState(false);

  // Apps state
  const [showAppsPanel, setShowAppsPanel] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [showMailbox, setShowMailbox] = useState(false);
  const [newSubmissionsCount, setNewSubmissionsCount] = useState(0);

  // Form states
  const [newEmail, setNewEmail] = useState('');
  const [emailChangeSent, setEmailChangeSent] = useState(false);
  const [enrichJson, setEnrichJson] = useState('');
  const [enriching, setEnriching] = useState(false);
  const [enrichMode, setEnrichMode] = useState('pdf'); // 'pdf' or 'json'
  const [enrichmentData, setEnrichmentData] = useState(null);
  const [selectedEnrichments, setSelectedEnrichments] = useState({});
  const pdfInputRef = useRef(null);

  // Horizontal order state
  const [horizontalOrder, setHorizontalOrder] = useState(DEFAULT_HORIZONTAL_ORDER);

  // Load page data
  const loadPage = useCallback(async () => {
    console.log('[EditorPage] Loading page data...');
    try {
      const data = isAdminMode 
        ? await adminGetPage(params.pageId)
        : await getMyPage();
      console.log('[EditorPage] Page loaded:', data?.id);
      setPage(data);
      
      // Initialize horizontal order from page data
      if (data?.horizontal_order && data.horizontal_order.length > 0) {
        // Auto-add any missing card types to horizontal order
        let order = [...data.horizontal_order];
        const cardTypeToSection = {
          'ANNOUNCEMENT': 'announcement',
          'STATS_GRID': 'stats',
          'HOW_I_INVEST': 'howIInvest',
          'INVESTMENT_FOCUS': 'investmentFocus',
          'POST_INVESTMENT_SUPPORT': 'postInvestment',
          'INVESTMENT_THESIS': 'investmentThesis',
          'CAROUSEL': 'carousel'
        };
        
        // Check for cards that exist but aren't in horizontal order
        for (const card of (data?.cards || [])) {
          let sectionId = cardTypeToSection[card.type];
          
          // Handle Empty cards with isHorizontal flag - use card ID as section ID
          if (card.type === 'EMPTY' && card.content?.isHorizontal) {
            sectionId = `empty_${card.id}`;
          }
          
          if (sectionId && !order.includes(sectionId)) {
            console.log(`[EditorPage] Auto-adding ${sectionId} to horizontal order at TOP`);
            // Insert at position 0 (top, just below hero) for new cards
            order.unshift(sectionId);
          }
        }
        setHorizontalOrder(order);
      } else {
        // Build default order based on existing cards - new cards at top
        const existingOrder = [];
        // Add carousel first if exists (top priority for new horizontal cards)
        if (data?.cards?.find(c => c.type === 'CAROUSEL')) existingOrder.push('carousel');
        if (data?.cards?.find(c => c.type === 'ANNOUNCEMENT')) existingOrder.push('announcement');
        if (data?.cards?.find(c => c.type === 'INVESTMENT_THESIS')) existingOrder.push('investmentThesis');
        if (data?.cards?.find(c => c.type === 'STATS_GRID')) existingOrder.push('stats');
        if (data?.cards?.find(c => c.type === 'HOW_I_INVEST')) existingOrder.push('howIInvest');
        if (data?.cards?.find(c => c.type === 'INVESTMENT_FOCUS')) existingOrder.push('investmentFocus');
        if (data?.cards?.find(c => c.type === 'POST_INVESTMENT_SUPPORT')) existingOrder.push('postInvestment');
        existingOrder.push('connect');
        setHorizontalOrder(existingOrder.length > 1 ? existingOrder : DEFAULT_HORIZONTAL_ORDER);
      }
    } catch (err) {
      console.error('[EditorPage] Error loading page:', err);
      toast.error('Failed to load page');
    } finally {
      setLoading(false);
    }
  }, [isAdminMode, params.pageId]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  // Fetch new submissions count for inbox badge
  useEffect(() => {
    const fetchNewSubmissionsCount = async () => {
      if (!page?.id) return;
      const hasApps = (page.apps || []).some(a => a.enabled);
      if (!hasApps) {
        setNewSubmissionsCount(0);
        return;
      }
      
      try {
        const data = await getPageSubmissions(page.id);
        // Count submissions that are 'new' OR have no status (legacy submissions)
        const newCount = (data.submissions || []).filter(s => 
          s.status === 'new' || !s.status
        ).length;
        setNewSubmissionsCount(newCount);
      } catch (err) {
        console.error('Failed to fetch submissions count:', err);
        setNewSubmissionsCount(0);
      }
    };
    
    fetchNewSubmissionsCount();
    // Poll every 30 seconds for new submissions
    const interval = setInterval(fetchNewSubmissionsCount, 30000);
    return () => clearInterval(interval);
  }, [page?.id, page?.apps]);

  // API wrapper functions based on mode
  const apiUpdatePage = isAdminMode ? adminUpdatePage : updatePage;
  const apiTogglePublish = isAdminMode ? adminTogglePublish : togglePublish;
  const apiAddCard = isAdminMode ? adminAddCard : addCard;
  const apiUpdateCard = isAdminMode ? adminUpdateCard : updateCard;
  const apiDeleteCard = isAdminMode ? adminDeleteCard : deleteCard;
  const apiReorderCards = isAdminMode ? adminReorderCards : reorderCards;
  const apiUploadPhoto = isAdminMode ? adminUploadPhoto : uploadPhoto;

  // Move horizontal section up/down
  const moveHorizontalSection = async (sectionId, direction) => {
    console.log('[EditorPage] Moving section:', sectionId, direction);
    const currentIndex = horizontalOrder.indexOf(sectionId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= horizontalOrder.length) return;

    const newOrder = [...horizontalOrder];
    [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];
    
    setHorizontalOrder(newOrder);
    
    try {
      await apiUpdatePage(page.id, { horizontal_order: newOrder });
      toast.success('Section moved');
    } catch (err) {
      console.error('[EditorPage] Error moving section:', err);
      toast.error('Failed to save order');
      setHorizontalOrder(horizontalOrder);
    }
  };

  // Handle save
  const handleSave = async (updates) => {
    console.log('[EditorPage] Saving updates:', Object.keys(updates));
    setSaving(true);
    try {
      const updated = await apiUpdatePage(page.id, updates);
      setPage(updated);
      toast.success('Saved');
    } catch (err) {
      console.error('[EditorPage] Save error:', err);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Handle publish toggle
  const handlePublish = async () => {
    console.log('[EditorPage] Toggling publish state');
    try {
      const result = await apiTogglePublish(page.id);
      // Update only the is_published field, not the entire page
      setPage(prev => ({ ...prev, is_published: result.is_published }));
      toast.success(result.is_published ? 'Page published!' : 'Page unpublished');
    } catch (err) {
      console.error('[EditorPage] Publish error:', err);
      toast.error('Failed to update publish status');
    }
  };

  // Handle preview
  const handlePreview = () => {
    const url = `/p/${page.slug}`;
    console.log('[EditorPage] Opening preview:', url);
    window.open(url, '_blank');
  };

  // Array move helper
  const arrayMove = (arr, fromIndex, toIndex) => {
    const result = [...arr];
    const [removed] = result.splice(fromIndex, 1);
    result.splice(toIndex, 0, removed);
    return result;
  };

  // Handle card move in two-column grid
  // Horizontal card types that should not be in the two-column grid
  const HORIZONTAL_CARD_TYPES = ['STATS_GRID', 'HOW_I_INVEST', 'INVESTMENT_FOCUS', 'POST_INVESTMENT_SUPPORT', 'ANNOUNCEMENT', 'INVESTMENT_THESIS', 'CAROUSEL'];
  
  // Check if a card should be horizontal (either by type or by content.isHorizontal flag)
  const isHorizontalCard = (card) => {
    if (HORIZONTAL_CARD_TYPES.includes(card.type)) return true;
    if (card.type === 'EMPTY' && card.content?.isHorizontal) return true;
    return false;
  };
  
  const handleMoveCard = async (cardId, direction) => {
    console.log('[EditorPage] Moving card:', cardId, direction);
    const cardsToReorder = page.cards.filter(c => !isHorizontalCard(c));
    const currentIndex = cardsToReorder.findIndex(c => c.id === cardId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= cardsToReorder.length) return;

    const reorderedCards = arrayMove([...cardsToReorder], currentIndex, newIndex);
    const horizontalCards = page.cards.filter(c => isHorizontalCard(c));
    const allCards = [...horizontalCards, ...reorderedCards].map((card, idx) => ({ ...card, order: idx }));

    setPage({ ...page, cards: allCards });
    try {
      await apiReorderCards(page.id, allCards.map(c => c.id));
      toast.success('Card moved');
    } catch (err) {
      console.error('[EditorPage] Error reordering cards:', err);
      toast.error('Failed to reorder');
    }
  };

  // Handle photo upload
  const handlePhotoUpload = async (file) => {
    console.log('[EditorPage] Uploading photo:', file.name);
    setUploadingPhoto(true);
    try {
      let result;
      if (isAdminMode) {
        // Admin mode: pass pageId and file
        result = await adminUploadPhoto(page.id, file);
      } else {
        // Regular mode: pass only file
        result = await uploadPhoto(file);
      }
      // Backend returns public_url (not photo_url)
      const photoUrl = result.public_url || result.photo_url;
      setPage({ ...page, hero: { ...page.hero, photo_url: photoUrl } });
      toast.success('Photo uploaded!');
    } catch (err) {
      console.error('[EditorPage] Photo upload error:', err);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Handle add card
  const handleAddCard = async (cardType) => {
    console.log('[EditorPage] Adding card:', cardType);
    const typeInfo = CARD_TYPES.find(t => t.type === cardType);
    if (!typeInfo) return;

    // Handle Links card specially
    if (cardType === 'LINKS') {
      if (!horizontalOrder.includes('connect')) {
        const newOrder = [...horizontalOrder, 'connect'];
        setHorizontalOrder(newOrder);
        await apiUpdatePage(page.id, { horizontal_order: newOrder });
        toast.success('Links section added');
      }
      setShowAddCard(false);
      return;
    }

    try {
      const newCard = await apiAddCard(page.id, {
        type: cardType,
        title: typeInfo.label,
        icon: typeInfo.icon,
        content: typeInfo.defaultContent || {}
      });
      
      setPage({ ...page, cards: [...page.cards, newCard] });
      
      // Add to horizontal order if it's a horizontal card type
      let sectionId = HORIZONTAL_TYPE_MAP[cardType];
      
      // Handle Empty cards with isHorizontal content
      if (cardType === 'EMPTY' && newCard.content?.isHorizontal) {
        sectionId = `empty_${newCard.id}`;
      }
      
      if (sectionId && !horizontalOrder.includes(sectionId)) {
        // Insert new horizontal cards at the TOP (position 0), just under hero
        const newOrder = [sectionId, ...horizontalOrder];
        setHorizontalOrder(newOrder);
        await apiUpdatePage(page.id, { horizontal_order: newOrder });
      }
      
      setShowAddCard(false);
      toast.success('Card added');
    } catch (err) {
      console.error('[EditorPage] Error adding card:', err);
      toast.error('Failed to add card');
    }
  };

  // Handle update card
  const handleUpdateCard = async (cardId, updates) => {
    console.log('[EditorPage] Updating card:', cardId);
    try {
      const updated = await apiUpdateCard(page.id, cardId, updates);
      setPage({ ...page, cards: page.cards.map(c => c.id === cardId ? updated : c) });
      setEditingCard(null);
      toast.success('Card updated');
    } catch (err) {
      console.error('[EditorPage] Update card error:', err);
      toast.error('Failed to update card');
    }
  };

  // Handle delete card
  const handleDeleteCard = async (card) => {
    console.log('[EditorPage] Deleting card:', card.id, card.type);
    if (!window.confirm('Delete this card?')) return;

    try {
      await apiDeleteCard(page.id, card.id);
      const updatedCards = page.cards.filter(c => c.id !== card.id);
      setPage({ ...page, cards: updatedCards });
      
      // Remove from horizontal order if applicable
      let sectionId = HORIZONTAL_TYPE_MAP[card.type];
      
      // Handle Empty cards which use dynamic section IDs
      if (card.type === 'EMPTY' && card.content?.isHorizontal) {
        sectionId = `empty_${card.id}`;
      }
      
      if (sectionId && horizontalOrder.includes(sectionId)) {
        const newOrder = horizontalOrder.filter(id => id !== sectionId);
        setHorizontalOrder(newOrder);
        await apiUpdatePage(page.id, { horizontal_order: newOrder });
      }
      
      toast.success('Card deleted');
    } catch (err) {
      console.error('[EditorPage] Delete card error:', err);
      toast.error('Failed to delete card');
    }
  };

  // Handle email change request
  const handleEmailChange = async () => {
    console.log('[EditorPage] Requesting email change to:', newEmail);
    if (!newEmail || !newEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    try {
      await requestEmailChange(newEmail);
      setEmailChangeSent(true);
      toast.success('Verification email sent to ' + newEmail);
    } catch (err) {
      console.error('[EditorPage] Email change error:', err);
      toast.error('Failed to send verification email');
    }
  };

  // Update hero field
  const updateHero = (field, value) => {
    console.log('[EditorPage] Updating hero field:', field);
    const newHero = { ...page.hero, [field]: value };
    setPage({ ...page, hero: newHero });
    handleSave({ hero: newHero });
  };

  // Save connect/links
  const saveConnectLinks = async (connectData) => {
    console.log('[EditorPage] Saving connect links');
    console.log('[EditorPage] Connect data to save:', JSON.stringify(connectData, null, 2));
    console.log('[EditorPage] Custom links count:', connectData?.custom_links?.length || 0);
    
    // Directly save the connect data (already updated in local state)
    await handleSave({ connect: connectData });
    setEditingConnect(false);
  };

  // Remove connect section
  const handleRemoveConnect = async () => {
    console.log('[EditorPage] Removing connect section');
    if (horizontalOrder.includes('connect')) {
      const newOrder = horizontalOrder.filter(id => id !== 'connect');
      setHorizontalOrder(newOrder);
      await apiUpdatePage(page.id, { horizontal_order: newOrder });
      toast.success('Links section removed');
    }
  };

  // Enrich from JSON
  const handleEnrichFromJson = async () => {
    console.log('[EditorPage] Enriching from JSON');
    if (!enrichJson.trim()) return;

    setEnriching(true);
    try {
      const data = JSON.parse(enrichJson);
      const updates = [];
      const newCards = [];

      // Helper to normalize keys
      const normalizeKey = (key) => key.toLowerCase().replace(/[\s_\-]/g, '');

      // Process investment focus
      const investFocusContent = { invest_in: [], do_not_invest_in: [] };
      const howIInvestContent = {
        lead_follow: [], primary_stage: [], check_size: [],
        ownership_target: [], hardware_software: [], b2b_b2c: [], geo_focus: []
      };

      const processValues = (value) => {
        if (Array.isArray(value)) return value;
        if (typeof value === 'string') return value.split(',').map(s => s.trim()).filter(Boolean);
        return [];
      };

      const processObject = (obj, prefix = '') => {
        for (const [key, value] of Object.entries(obj)) {
          const normalizedKey = normalizeKey(key);
          
          // Investment Focus mappings
          if (['investin', 'sectors', 'industries', 'verticals', 'themes', 'investmentfocus'].includes(normalizedKey)) {
            investFocusContent.invest_in.push(...processValues(value));
          } else if (['donotinvestin', 'avoid', 'exclude'].includes(normalizedKey)) {
            investFocusContent.do_not_invest_in.push(...processValues(value));
          }
          // How I Invest mappings
          else if (['leadfollow', 'lead', 'follow'].includes(normalizedKey)) {
            howIInvestContent.lead_follow.push(...processValues(value));
          } else if (['primarystage', 'stage', 'primarystagefocus'].includes(normalizedKey)) {
            howIInvestContent.primary_stage.push(...processValues(value));
          } else if (['checksize', 'ticketsize'].includes(normalizedKey)) {
            howIInvestContent.check_size.push(...processValues(value));
          } else if (['ownershiptarget', 'ownership'].includes(normalizedKey)) {
            howIInvestContent.ownership_target.push(...processValues(value));
          } else if (['hardwaresoftware', 'markettype'].includes(normalizedKey)) {
            howIInvestContent.hardware_software.push(...processValues(value));
          } else if (['b2bb2c', 'businessmodel'].includes(normalizedKey)) {
            howIInvestContent.b2b_b2c.push(...processValues(value));
          } else if (['geofocus', 'geography', 'geo'].includes(normalizedKey)) {
            howIInvestContent.geo_focus.push(...processValues(value));
          }
          // Recurse into nested objects
          else if (typeof value === 'object' && !Array.isArray(value)) {
            processObject(value, key);
          }
        }
      };

      processObject(data);

      // Update or create Investment Focus card
      if (investFocusContent.invest_in.length > 0 || investFocusContent.do_not_invest_in.length > 0) {
        const existingCard = page.cards.find(c => c.type === 'INVESTMENT_FOCUS');
        if (existingCard) {
          const merged = {
            invest_in: [...new Set([...(existingCard.content?.invest_in || []), ...investFocusContent.invest_in])],
            do_not_invest_in: [...new Set([...(existingCard.content?.do_not_invest_in || []), ...investFocusContent.do_not_invest_in])]
          };
          await apiUpdateCard(page.id, existingCard.id, { content: merged });
          updates.push('Investment Focus');
        } else {
          const newCard = await apiAddCard(page.id, {
            type: 'INVESTMENT_FOCUS', title: 'Investment Focus', icon: '🎯', content: investFocusContent
          });
          newCards.push(newCard);
        }
      }

      // Update or create How I Invest card
      const hasHowIInvestData = Object.values(howIInvestContent).some(arr => arr.length > 0);
      if (hasHowIInvestData) {
        const existingCard = page.cards.find(c => c.type === 'HOW_I_INVEST');
        if (existingCard) {
          const merged = {};
          for (const key of Object.keys(howIInvestContent)) {
            merged[key] = [...new Set([...(existingCard.content?.[key] || []), ...howIInvestContent[key]])];
          }
          await apiUpdateCard(page.id, existingCard.id, { content: merged });
          updates.push('How I Invest');
        } else {
          const newCard = await apiAddCard(page.id, {
            type: 'HOW_I_INVEST', title: 'How I Invest', icon: '💰', content: howIInvestContent
          });
          newCards.push(newCard);
        }
      }

      if (newCards.length > 0) {
        setPage({ ...page, cards: [...page.cards, ...newCards] });
      }

      await loadPage();

      if (updates.length > 0 || newCards.length > 0) {
        toast.success(`Enriched: ${[...updates, ...newCards.map(c => c.type.replace(/_/g, ' '))].join(', ')}`);
        setShowEnrichDialog(false);
        setEnrichJson('');
      } else {
        toast.info('No matching fields found in the JSON');
      }
    } catch (err) {
      console.error('[EditorPage] JSON enrich error:', err);
      toast.error('Invalid JSON format');
    } finally {
      setEnriching(false);
    }
  };

  // Handle PDF file selection for enrichment
  const handlePdfSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.includes('pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }

    setEnriching(true);
    setEnrichmentData(null);
    setSelectedEnrichments({});
    
    try {
      console.log('[EditorPage] Extracting from PDF:', file.name);
      const response = await adminEnrichFromPdf(file);
      console.log('[EditorPage] Response from API:', response);
      
      // Extract the enrichment data from the response
      const rawData = response.enrichment || response;
      console.log('[EditorPage] Raw enrichment data:', rawData);
      
      // Transform the data into the expected format for the UI
      const extracted = {
        // Hero section data
        hero: {
          subtitle: rawData.subtitle || '',
          badge: rawData.suggested_badge || '',
          name: rawData.name || '',
          location: rawData.location || '',
        },
        // Contact info
        contact: {
          email: rawData.email || rawData.contact?.email || '',
          linkedin_url: rawData.linkedin_url || rawData.contact?.linkedin_url || '',
        },
        // Suggested tags
        suggested_tags: rawData.suggested_tags || [],
        // Transform experience into journey cards
        cards: [],
        // Raw data for reference
        experience: rawData.experience || rawData.journey || [],
        education: rawData.education || [],
        skills: rawData.skills || [],
        stats: rawData.stats || [],
      };
      
      // Create a JOURNEY_TIMELINE card if there's experience data
      // Format: Company name as title, Role as subtitle (matching admin create profile)
      const experienceData = rawData.experience || rawData.journey || [];
      if (experienceData.length > 0) {
        extracted.cards.push({
          type: 'JOURNEY_TIMELINE',
          title: 'the journey',
          icon: '🚀',
          content: {
            items: experienceData.map(exp => ({
              title: exp.company || exp.role,  // Company name as main title
              subtitle: exp.role || '',         // Role as subtitle
              description: exp.description || '',
              date: exp.duration || '',
              icon: exp.is_current ? '🔥' : '💼',
              is_current: exp.is_current || false
            }))
          }
        });
      }
      
      // Create education items for the journey
      // Format: Institution as title, Degree as subtitle
      if (rawData.education?.length > 0) {
        const eduItems = rawData.education.map(edu => ({
          title: edu.institution || edu.degree,  // Institution as main title
          subtitle: edu.degree || '',             // Degree as subtitle
          description: edu.field_of_study || '',
          date: edu.year || '',
          icon: '🎓',
          is_current: false
        }));
        extracted.educationItems = eduItems;
      }
      
      // Create STATS_GRID card if there's stats data
      if (rawData.stats?.length > 0) {
        extracted.cards.push({
          type: 'STATS_GRID',
          title: 'by the numbers',
          icon: '📊',
          content: {
            stats: rawData.stats
          }
        });
      }
      
      console.log('[EditorPage] Transformed extracted data:', extracted);
      setEnrichmentData(extracted);
      
      // Pre-select all fields by default
      const selections = {};
      if (extracted.contact?.linkedin_url) selections.linkedin = true;
      if (extracted.hero?.subtitle || extracted.hero?.badge) selections.hero = true;
      if (extracted.experience?.length) selections.journey = true;
      if (extracted.education?.length) selections.education = true;
      if (extracted.suggested_tags?.length) selections.tags = true;
      if (extracted.stats?.length) selections.stats = true;
      setSelectedEnrichments(selections);
      
      toast.success('PDF extracted! Review and apply the enrichments below.');
    } catch (err) {
      console.error('[EditorPage] PDF extraction error:', err);
      toast.error(err.response?.data?.detail || 'Failed to extract from PDF');
    } finally {
      setEnriching(false);
      // Reset file input
      if (pdfInputRef.current) pdfInputRef.current.value = '';
    }
  };

  // Apply selected enrichments from PDF
  const handleApplyEnrichments = async () => {
    if (!enrichmentData) return;
    
    setEnriching(true);
    try {
      const updates = [];
      
      // Apply LinkedIn URL - ensure it has https:// prefix
      if (selectedEnrichments.linkedin && enrichmentData.contact?.linkedin_url) {
        let linkedinUrl = enrichmentData.contact.linkedin_url;
        // Add https:// if missing
        if (linkedinUrl && !linkedinUrl.startsWith('http://') && !linkedinUrl.startsWith('https://')) {
          linkedinUrl = 'https://' + linkedinUrl;
        }
        await apiUpdatePage(page.id, { 
          connect: { ...page.connect, linkedin_url: linkedinUrl } 
        });
        updates.push('LinkedIn URL');
      }
      
      // Apply hero updates (subtitle/bio, badge)
      if (selectedEnrichments.hero && enrichmentData.hero) {
        const hero = enrichmentData.hero;
        
        // Only send the fields we want to update - don't spread existing hero
        // MongoDB $set will merge with existing document
        const heroUpdates = {};
        
        // Update bio/subtitle - this is the short description shown on profile
        if (hero.subtitle) {
          heroUpdates.subtitle = hero.subtitle;
          heroUpdates.bio = hero.subtitle; // Also update bio field
          console.log('[EditorPage] Applying subtitle:', hero.subtitle);
        }
        if (hero.badge) {
          heroUpdates.badge = hero.badge;
          console.log('[EditorPage] Applying badge:', hero.badge);
        }
        if (hero.location) {
          heroUpdates.location = hero.location;
        }
        
        // Only send update if we have fields to update
        if (Object.keys(heroUpdates).length > 0) {
          console.log('[EditorPage] Saving hero updates:', heroUpdates);
          await apiUpdatePage(page.id, { hero: heroUpdates });
          updates.push('Profile Info');
        }
      }
      
      // Apply Journey (work experience) - Create or update JOURNEY_TIMELINE card
      if (selectedEnrichments.journey && enrichmentData.experience?.length > 0) {
        const journeyItems = enrichmentData.experience.map(exp => ({
          title: exp.company || exp.role,
          subtitle: exp.role || '',
          description: exp.description || '',
          date: exp.duration || '',
          icon: exp.is_current ? '🔥' : '💼',
          is_current: exp.is_current || false
        }));
        
        // Append education items if also selected
        if (selectedEnrichments.education && enrichmentData.education?.length > 0) {
          enrichmentData.education.forEach(edu => {
            journeyItems.push({
              title: edu.institution,
              subtitle: edu.degree || '',
              description: edu.field_of_study || '',
              date: edu.year || '',
              icon: '🎓',
              is_current: false
            });
          });
        }
        
        const existingJourneyCard = page.cards.find(c => c.type === 'JOURNEY_TIMELINE');
        if (existingJourneyCard) {
          // Update existing journey card
          await apiUpdateCard(page.id, existingJourneyCard.id, {
            content: { items: journeyItems }
          });
        } else {
          // Create new journey card
          await apiAddCard(page.id, {
            type: 'JOURNEY_TIMELINE',
            title: 'the journey',
            icon: '🚀',
            content: { items: journeyItems }
          });
        }
        updates.push('Journey');
      } else if (selectedEnrichments.education && enrichmentData.education?.length > 0 && !selectedEnrichments.journey) {
        // Education only (without journey) - Add to existing journey card or create new one
        const eduItems = enrichmentData.education.map(edu => ({
          title: edu.institution,
          subtitle: edu.degree || '',
          description: edu.field_of_study || '',
          date: edu.year || '',
          icon: '🎓',
          is_current: false
        }));
        
        const existingJourneyCard = page.cards.find(c => c.type === 'JOURNEY_TIMELINE');
        if (existingJourneyCard) {
          // Append education to existing journey
          const existingItems = existingJourneyCard.content?.items || [];
          await apiUpdateCard(page.id, existingJourneyCard.id, {
            content: { items: [...existingItems, ...eduItems] }
          });
        } else {
          // Create new journey card with just education
          await apiAddCard(page.id, {
            type: 'JOURNEY_TIMELINE',
            title: 'the journey',
            icon: '🎓',
            content: { items: eduItems }
          });
        }
        updates.push('Education');
      }
      
      // Apply tags - only send tags, don't overwrite other hero fields
      if (selectedEnrichments.tags && enrichmentData.suggested_tags?.length) {
        const existingTags = page.hero?.tags || [];
        const newTags = [...new Set([...existingTags, ...enrichmentData.suggested_tags])];
        await apiUpdatePage(page.id, { hero: { tags: newTags } });
        updates.push('Tags');
      }
      
      // Apply Stats / Accomplishments - Create or update STATS_GRID card
      if (selectedEnrichments.stats && enrichmentData.stats?.length > 0) {
        const existingStatsCard = page.cards.find(c => c.type === 'STATS_GRID');
        if (existingStatsCard) {
          await apiUpdateCard(page.id, existingStatsCard.id, {
            content: { stats: enrichmentData.stats }
          });
        } else {
          await apiAddCard(page.id, {
            type: 'STATS_GRID',
            title: 'by the numbers',
            icon: '📊',
            content: { stats: enrichmentData.stats }
          });
        }
        updates.push('Accomplishments');
      }
      
      await loadPage();
      
      if (updates.length > 0) {
        toast.success(`Applied: ${updates.join(', ')}`);
        setShowEnrichDialog(false);
        setEnrichmentData(null);
        setSelectedEnrichments({});
      } else {
        toast.info('No enrichments selected');
      }
    } catch (err) {
      console.error('[EditorPage] Apply enrichments error:', err);
      toast.error('Failed to apply enrichments');
    } finally {
      setEnriching(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: cssVars.bg }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: cssVars.accent }} />
      </div>
    );
  }

  // No page found - offer to create one
  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: cssVars.bg }}>
        <div className="text-center max-w-md">
          <h2 className="text-xl font-semibold mb-2">No profile yet</h2>
          <p className="text-gray-500 mb-4">You haven&apos;t created your Brief profile yet.</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate('/create')} className="bg-[#1a1a1a] text-white">
              Create Profile
            </Button>
            <Button variant="outline" onClick={() => navigate('/')}>
              Go Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Extract card types
  const cards = (page.cards || []).sort((a, b) => (a.order || 0) - (b.order || 0));
  const announcementCard = cards.find(c => c.type === 'ANNOUNCEMENT');
  const investmentThesisCard = cards.find(c => c.type === 'INVESTMENT_THESIS');
  const statsCard = cards.find(c => c.type === 'STATS_GRID');
  const howIInvestCard = cards.find(c => c.type === 'HOW_I_INVEST');
  const investmentFocusCard = cards.find(c => c.type === 'INVESTMENT_FOCUS');
  const postInvestmentCard = cards.find(c => c.type === 'POST_INVESTMENT_SUPPORT');
  const highlightsCard = cards.find(c => c.type === 'HIGHLIGHTS');
  const carouselCard = cards.find(c => c.type === 'CAROUSEL');
  
  // Cards for two-column grid (exclude horizontal cards and highlights)
  const gridCards = cards.filter(c => !isHorizontalCard(c) && c.type !== 'HIGHLIGHTS');
  const leftCards = gridCards.filter((_, i) => i % 2 === 0);
  const rightCards = gridCards.filter((_, i) => i % 2 === 1);

  return (
    <div style={{ background: cssVars.bg, minHeight: '100vh', padding: '20px' }}>
      {/* Admin Mode Banner */}
      {isAdminMode && (
        <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white text-center py-2 text-sm font-medium z-50">
          Admin Mode - Editing {page.hero?.name || 'User'}&apos;s page
          <Link to="/admin" className="ml-4 underline">← Back to Admin</Link>
        </div>
      )}

      {/* Floating Toolbar */}
      {/* Hide toolbar when Pitch Form Builder is open */}
      {editingApp?.type !== APP_TYPES.PITCH && (
        <EditorToolbar
          isAdminMode={isAdminMode}
          isPublished={page.is_published}
          onAddCard={(cardType) => cardType ? handleAddCard(cardType) : setShowAddCard(true)}
          onEnrich={() => setShowEnrichDialog(true)}
          onSettings={() => setShowSettings(true)}
          onPreview={handlePreview}
          onPublish={handlePublish}
          existingCardTypes={cards.map(c => c.type)}
          onOpenApps={() => setShowAppsPanel(true)}
          hasApps={(page.apps || []).some(a => a.enabled)}
          onOpenMailbox={() => navigate(`/deals?pageId=${page.id}`)}
          newSubmissionsCount={newSubmissionsCount}
        />
      )}

      {/* Main Content */}
      <div style={{ maxWidth: '900px', margin: isAdminMode ? '60px auto 0' : '0 auto' }}>
        {/* Hero Section */}
        <HeroSection
          hero={page.hero}
          connect={page.connect}
          onUpdateHero={updateHero}
          onPhotoUpload={handlePhotoUpload}
          onEditSocials={() => setEditingSocials(true)}
          uploadingPhoto={uploadingPhoto}
          apps={page.apps}
          slug={page.slug}
          onEditPitchApp={async () => {
            // Find or create pitch app
            const pitchApp = page.apps?.find(a => a.type === 'pitch');
            if (pitchApp) {
              setEditingApp(pitchApp);
            } else {
              // Create default pitch app if it doesn't exist
              const APP_DEFINITIONS_LOCAL = await import('../components/apps/appConstants').then(m => m.APP_DEFINITIONS);
              const pitchDef = APP_DEFINITIONS_LOCAL.find(a => a.type === 'pitch');
              if (pitchDef) {
                try {
                  const newApp = {
                    id: `app_${Date.now()}`,
                    type: 'pitch',
                    enabled: false, // Start disabled
                    button_text: pitchDef.buttonText,
                    button_icon: pitchDef.icon,
                    title: pitchDef.name,
                    description: pitchDef.description,
                    fields: pitchDef.defaultFields.map(f => ({ ...f }))
                  };
                  const result = await addAppToPage(page.id, newApp);
                  setPage(prev => ({ ...prev, apps: result.apps }));
                  // Now open the editor with the newly created app
                  const createdApp = result.apps.find(a => a.type === 'pitch');
                  if (createdApp) {
                    setEditingApp(createdApp);
                  }
                } catch (err) {
                  console.error('Failed to create pitch app:', err);
                  toast.error('Failed to create pitch form');
                }
              }
            }
          }}
          onOpenAppsPanel={() => setShowAppsPanel(true)}
          onEditApp={(app) => setEditingApp(app)}
        />

        {/* Horizontal Sections */}
        <HorizontalSections
          horizontalOrder={horizontalOrder}
          announcementCard={announcementCard}
          investmentThesisCard={investmentThesisCard}
          statsCard={statsCard}
          howIInvestCard={howIInvestCard}
          investmentFocusCard={investmentFocusCard}
          postInvestmentCard={postInvestmentCard}
          highlightsCard={highlightsCard}
          carouselCard={carouselCard}
          emptyCards={cards.filter(c => c.type === 'EMPTY' && c.content?.isHorizontal)}
          connect={page.connect}
          onMoveSection={moveHorizontalSection}
          onEditAnnouncement={() => setEditingAnnouncement(true)}
          onEditInvestmentThesis={() => setEditingInvestmentThesis(true)}
          onEditStats={() => setEditingStats(true)}
          onEditHowIInvest={() => setEditingHowIInvest(true)}
          onEditInvestmentFocus={() => setEditingInvestmentFocus(true)}
          onEditPostInvestment={() => setEditingPostInvestment(true)}
          onEditHighlights={() => highlightsCard && setEditingCard(highlightsCard)}
          onEditCarousel={() => carouselCard && setEditingCard(carouselCard)}
          onEditEmptyCard={(card) => setEditingCard(card)}
          onEditConnect={() => setEditingConnect(true)}
          onDeleteCard={handleDeleteCard}
          onUpdateCard={handleUpdateCard}
          onRemoveConnect={handleRemoveConnect}
        />

        {/* Two Column Grid */}
        <div className="cards-grid">
          <div className="flex flex-col gap-4">
            {leftCards.map((card, idx) => (
              <CardWrapper
                key={card.id}
                card={card}
                index={idx * 2}
                totalCards={gridCards.length}
                onEdit={() => setEditingCard(card)}
                onDelete={handleDeleteCard}
                onMoveUp={() => handleMoveCard(card.id, 'up')}
                onMoveDown={() => handleMoveCard(card.id, 'down')}
              >
                <InfoCard card={card} />
              </CardWrapper>
            ))}
          </div>
          <div className="flex flex-col gap-4">
            {rightCards.map((card, idx) => (
              <CardWrapper
                key={card.id}
                card={card}
                index={idx * 2 + 1}
                totalCards={gridCards.length}
                onEdit={() => setEditingCard(card)}
                onDelete={handleDeleteCard}
                onMoveUp={() => handleMoveCard(card.id, 'up')}
                onMoveDown={() => handleMoveCard(card.id, 'down')}
              >
                <InfoCard card={card} />
              </CardWrapper>
            ))}
          </div>
        </div>
      </div>

      {/* ===== DIALOGS ===== */}
      
      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={(open) => { setShowSettings(open); if (!open) { setNewEmail(''); setEmailChangeSent(false); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Page Settings</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Page URL</label>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-gray-500 text-sm">/p/</span>
                <Input 
                  value={page.slug} 
                  onChange={(e) => setPage({ ...page, slug: e.target.value })}
                  onBlur={() => handleSave({ slug: page.slug })}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">
                {isAdminMode ? 'Change User Email' : 'Change Email'}
              </label>
              <p className="text-xs text-gray-500 mb-2">
                {isAdminMode 
                  ? 'This will send a verification link to the new email address'
                  : 'This will send a verification link to your new email'
                }
              </p>
              {emailChangeSent ? (
                <p className="text-green-600 text-sm">Verification email sent! Check inbox.</p>
              ) : (
                <div className="flex gap-2">
                  <Input 
                    type="email" 
                    value={newEmail} 
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="new@email.com"
                  />
                  <Button onClick={handleEmailChange}>Send Link</Button>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Card Dialog */}
      <Dialog open={showAddCard} onOpenChange={setShowAddCard}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add a Card</DialogTitle></DialogHeader>
          <div className="grid gap-2 mt-4 max-h-[60vh] overflow-y-auto">
            {(() => {
              // Pre-compute existing card types for consistent checks
              const existingCardTypes = new Set((page?.cards || []).map(c => c.type));
              const hasConnectSection = horizontalOrder.includes('connect');
              
              // Log current state when dialog is open
              if (showAddCard) {
                console.log('[AddCardDialog] Current cards:', [...existingCardTypes]);
                console.log('[AddCardDialog] Connect section exists:', hasConnectSection);
              }
              
              return CARD_TYPES.map(type => {
                // Check if this card type can be added
                const isLinks = type.type === 'LINKS';
                const singletonTypes = ['STATS_GRID', 'HOW_I_INVEST', 'INVESTMENT_FOCUS', 'POST_INVESTMENT_SUPPORT', 'ANNOUNCEMENT', 'INVESTMENT_THESIS'];
                const isSingleton = singletonTypes.includes(type.type);
                
                // Determine if disabled
                const cardExists = existingCardTypes.has(type.type);
                let isDisabled = false;
                let disabledReason = '';
                
                if (isLinks && hasConnectSection) {
                  isDisabled = true;
                  disabledReason = 'Already added';
                } else if (isSingleton && cardExists) {
                  isDisabled = true;
                  disabledReason = 'Already added';
                }
                
                return (
                  <button
                    key={type.type}
                    onClick={() => !isDisabled && handleAddCard(type.type)}
                    disabled={isDisabled}
                    className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                      isDisabled 
                        ? 'bg-gray-100 opacity-60 cursor-not-allowed border-gray-300' 
                        : 'hover:bg-gray-50 cursor-pointer border-gray-200'
                    }`}
                  >
                    <span className={`text-2xl ${isDisabled ? 'grayscale opacity-50' : ''}`}>{type.icon}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className={`font-medium ${isDisabled ? 'text-gray-400' : 'text-gray-900'}`}>{type.label}</p>
                        {isDisabled && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                            {disabledReason}
                          </span>
                        )}
                      </div>
                      <p className={`text-sm ${isDisabled ? 'text-gray-400' : 'text-gray-500'}`}>{type.description}</p>
                    </div>
                  </button>
                );
              });
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Social Links Dialog */}
      <Dialog open={editingSocials} onOpenChange={setEditingSocials}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Edit Social Links</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            {PROFILE_SOCIAL_OPTIONS.map(opt => (
              <div key={opt.key}>
                <label className="text-sm font-medium text-gray-700">{opt.label}</label>
                <Input
                  value={page.connect?.[opt.key] || ''}
                  onChange={(e) => setPage({ ...page, connect: { ...page.connect, [opt.key]: e.target.value } })}
                  placeholder={opt.placeholder}
                  className="mt-1"
                />
              </div>
            ))}
            <Button onClick={() => { handleSave({ connect: page.connect }); setEditingSocials(false); }} className="w-full">Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Links Dialog */}
      <Dialog open={editingConnect} onOpenChange={setEditingConnect}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Links</DialogTitle></DialogHeader>
          <div className="space-y-6 mt-4">
            {/* Standard Links */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">Standard Links</h3>
              {LINKS_OPTIONS.map(opt => (
                <div key={opt.key}>
                  <label className="text-sm font-medium text-gray-700">{opt.label}</label>
                  <Input
                    value={page.connect?.[opt.key] || ''}
                    onChange={(e) => setPage({ ...page, connect: { ...page.connect, [opt.key]: e.target.value } })}
                    placeholder={opt.placeholder}
                    className="mt-1"
                  />
                </div>
              ))}
            </div>

            {/* Custom Links */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Custom Links</h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const customLinks = page.connect?.custom_links || [];
                    setPage({ 
                      ...page, 
                      connect: { 
                        ...page.connect, 
                        custom_links: [...customLinks, { title: '', url: '', image_url: '' }] 
                      } 
                    });
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Link
                </Button>
              </div>
              
              {(page.connect?.custom_links || []).map((link, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <Input
                      value={link.title || ''}
                      onChange={(e) => {
                        const customLinks = [...(page.connect?.custom_links || [])];
                        customLinks[idx] = { ...customLinks[idx], title: e.target.value };
                        setPage({ ...page, connect: { ...page.connect, custom_links: customLinks } });
                      }}
                      placeholder="Link title (e.g., My Blog)"
                      className="flex-1"
                    />
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => {
                        const customLinks = (page.connect?.custom_links || []).filter((_, i) => i !== idx);
                        setPage({ ...page, connect: { ...page.connect, custom_links: customLinks } });
                      }}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={link.url || ''}
                      onChange={(e) => {
                        const customLinks = [...(page.connect?.custom_links || [])];
                        customLinks[idx] = { ...customLinks[idx], url: e.target.value };
                        setPage({ ...page, connect: { ...page.connect, custom_links: customLinks } });
                      }}
                      placeholder="https://example.com"
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!link.url || link.unfurling}
                      onClick={async () => {
                        if (!link.url) return;
                        const customLinks = [...(page.connect?.custom_links || [])];
                        customLinks[idx] = { ...customLinks[idx], unfurling: true };
                        setPage({ ...page, connect: { ...page.connect, custom_links: customLinks } });
                        
                        try {
                          const result = await unfurlLink(link.url);
                          if (result.success && result.data) {
                            const updatedLinks = [...(page.connect?.custom_links || [])];
                            updatedLinks[idx] = {
                              ...updatedLinks[idx],
                              title: updatedLinks[idx].title || result.data.title || '',
                              image_url: result.data.image || updatedLinks[idx].image_url || '',
                              unfurling: false
                            };
                            setPage({ ...page, connect: { ...page.connect, custom_links: updatedLinks } });
                            toast.success('Link data fetched!');
                          } else {
                            const updatedLinks = [...(page.connect?.custom_links || [])];
                            updatedLinks[idx] = { ...updatedLinks[idx], unfurling: false };
                            setPage({ ...page, connect: { ...page.connect, custom_links: updatedLinks } });
                            toast.error('Could not fetch link data');
                          }
                        } catch (err) {
                          const updatedLinks = [...(page.connect?.custom_links || [])];
                          updatedLinks[idx] = { ...updatedLinks[idx], unfurling: false };
                          setPage({ ...page, connect: { ...page.connect, custom_links: updatedLinks } });
                          toast.error('Failed to fetch link data');
                        }
                      }}
                    >
                      {link.unfurling ? <Loader2 className="w-4 h-4 animate-spin" /> : '🔍 Fetch'}
                    </Button>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Image URL (auto-fetched or manual)</label>
                    <Input
                      value={link.image_url || ''}
                      onChange={(e) => {
                        const customLinks = [...(page.connect?.custom_links || [])];
                        customLinks[idx] = { ...customLinks[idx], image_url: e.target.value };
                        setPage({ ...page, connect: { ...page.connect, custom_links: customLinks } });
                      }}
                      placeholder="https://example.com/image.png"
                    />
                  </div>
                  {link.image_url && (
                    <div className="mt-2">
                      <img 
                        src={link.image_url} 
                        alt={link.title} 
                        className="w-16 h-16 object-cover rounded-lg border"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <Button onClick={() => saveConnectLinks(page.connect)} className="w-full bg-[#1a1a1a]">Save Links</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Stats Dialog */}
      <StatsEditorDialog
        open={editingStats}
        onOpenChange={setEditingStats}
        statsCard={statsCard}
        onSave={(updates) => handleUpdateCard(statsCard?.id, updates)}
      />

      {/* Edit How I Invest Dialog */}
      <HowIInvestEditorDialog
        open={editingHowIInvest}
        onOpenChange={setEditingHowIInvest}
        card={howIInvestCard}
        onSave={(updates) => handleUpdateCard(howIInvestCard?.id, updates)}
      />

      {/* Edit Investment Focus Dialog */}
      <InvestmentFocusEditorDialog
        open={editingInvestmentFocus}
        onOpenChange={setEditingInvestmentFocus}
        card={investmentFocusCard}
        onSave={(updates) => handleUpdateCard(investmentFocusCard?.id, updates)}
      />

      {/* Edit Post Investment Dialog */}
      <PostInvestmentEditorDialog
        open={editingPostInvestment}
        onOpenChange={setEditingPostInvestment}
        card={postInvestmentCard}
        onSave={(updates) => handleUpdateCard(postInvestmentCard?.id, updates)}
      />

      {/* Edit Announcement Dialog */}
      <AnnouncementEditorDialog
        open={editingAnnouncement}
        onOpenChange={setEditingAnnouncement}
        card={announcementCard}
        onSave={(updates) => handleUpdateCard(announcementCard?.id, updates)}
      />

      {/* Edit Investment Thesis Dialog */}
      <InvestmentThesisEditorDialog
        open={editingInvestmentThesis}
        onOpenChange={setEditingInvestmentThesis}
        card={investmentThesisCard}
        onSave={(updates) => handleUpdateCard(investmentThesisCard?.id, updates)}
      />

      {/* Card Editor Dialog */}
      {editingCard && (
        <Dialog open={!!editingCard} onOpenChange={() => setEditingCard(null)}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit {CARD_TYPES.find(t => t.type === editingCard.type)?.label || 'Card'}</DialogTitle>
            </DialogHeader>
            <CardEditor
              key={`card-editor-${editingCard.id}-${JSON.stringify(editingCard.content?.items?.length || 0)}`}
              card={editingCard}
              onSave={(updates) => handleUpdateCard(editingCard.id, updates)}
              onCancel={() => setEditingCard(null)}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Enrich Dialog */}
      <Dialog open={showEnrichDialog} onOpenChange={(open) => { 
        setShowEnrichDialog(open); 
        if (!open) { 
          setEnrichJson(''); 
          setEnrichmentData(null); 
          setSelectedEnrichments({}); 
          setEnrichMode('pdf');
        } 
      }}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>✨</span> Enrich Profile
            </DialogTitle>
          </DialogHeader>
          
          {/* Mode Toggle */}
          <div className="flex gap-2 mt-2 p-1 bg-gray-100 rounded-lg">
            <button
              onClick={() => setEnrichMode('pdf')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                enrichMode === 'pdf' 
                  ? 'bg-white shadow-sm text-gray-900' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText className="w-4 h-4" />
              Upload PDF
            </button>
            <button
              onClick={() => setEnrichMode('json')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                enrichMode === 'json' 
                  ? 'bg-white shadow-sm text-gray-900' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="text-lg">{ }</span>
              Paste JSON
            </button>
          </div>

          {/* PDF Mode */}
          {enrichMode === 'pdf' && (
            <div className="space-y-4 mt-4">
              <p className="text-sm text-gray-600">
                Upload a LinkedIn PDF or resume to automatically extract and enrich your profile.
              </p>
              
              {/* Upload Area */}
              {!enrichmentData && (
                <div 
                  className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-violet-300 hover:bg-violet-50/30 transition-all cursor-pointer"
                  onClick={() => pdfInputRef.current?.click()}
                >
                  <input
                    ref={pdfInputRef}
                    type="file"
                    accept=".pdf"
                    onChange={handlePdfSelect}
                    className="hidden"
                  />
                  {enriching ? (
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-10 h-10 animate-spin text-violet-500" />
                      <p className="text-sm text-gray-600">Extracting profile data...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-14 h-14 rounded-full bg-violet-100 flex items-center justify-center">
                        <Upload className="w-7 h-7 text-violet-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Upload LinkedIn PDF</p>
                        <p className="text-sm text-gray-500 mt-1">Click or drag & drop</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Enrichment Preview */}
              {enrichmentData && (
                <div className="space-y-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">Review extracted data and select what to apply:</p>
                  
                  {/* LinkedIn URL - Most Important */}
                  {enrichmentData.contact?.linkedin_url && (
                    <label className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors border border-blue-200">
                      <input
                        type="checkbox"
                        checked={selectedEnrichments.linkedin || false}
                        onChange={(e) => setSelectedEnrichments(s => ({ ...s, linkedin: e.target.checked }))}
                        className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-blue-900 flex items-center gap-2">
                          <span>🔗</span> LinkedIn URL
                        </p>
                        <p className="text-sm text-blue-700 truncate mt-1">
                          {enrichmentData.contact.linkedin_url}
                        </p>
                      </div>
                    </label>
                  )}
                  
                  {/* Short Bio / Subtitle */}
                  {(enrichmentData.hero?.subtitle || enrichmentData.hero?.badge) && (
                    <label className="flex items-start gap-3 p-3 bg-violet-50 rounded-lg cursor-pointer hover:bg-violet-100 transition-colors border border-violet-200">
                      <input
                        type="checkbox"
                        checked={selectedEnrichments.hero || false}
                        onChange={(e) => setSelectedEnrichments(s => ({ ...s, hero: e.target.checked }))}
                        className="mt-1 w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-violet-900 flex items-center gap-2">
                          <span>✍️</span> Short Bio
                        </p>
                        {enrichmentData.hero?.subtitle && (
                          <p className="text-sm text-violet-700 mt-1 line-clamp-2">
                            {enrichmentData.hero.subtitle}
                          </p>
                        )}
                        {enrichmentData.hero?.badge && (
                          <p className="text-xs text-violet-600 mt-1">
                            <span className="bg-violet-200 px-2 py-0.5 rounded-full">Badge: {enrichmentData.hero.badge}</span>
                          </p>
                        )}
                      </div>
                    </label>
                  )}
                  
                  {/* Journey - Work Experience */}
                  {enrichmentData.experience?.length > 0 && (
                    <label className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors border border-amber-200">
                      <input
                        type="checkbox"
                        checked={selectedEnrichments.journey || false}
                        onChange={(e) => setSelectedEnrichments(s => ({ ...s, journey: e.target.checked }))}
                        className="mt-1 w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-amber-900 flex items-center gap-2">
                          <span>💼</span> Work Experience ({enrichmentData.experience.length} roles)
                        </p>
                        <div className="mt-2 space-y-1.5">
                          {enrichmentData.experience.slice(0, 3).map((exp, i) => (
                            <div key={i} className="text-sm">
                              <span className="font-medium text-amber-800">{exp.company}</span>
                              {exp.role && <span className="text-amber-600"> • {exp.role}</span>}
                              {exp.duration && <span className="text-amber-500 text-xs ml-1">({exp.duration})</span>}
                            </div>
                          ))}
                          {enrichmentData.experience.length > 3 && (
                            <p className="text-xs text-amber-500">+{enrichmentData.experience.length - 3} more...</p>
                          )}
                        </div>
                      </div>
                    </label>
                  )}
                  
                  {/* Education */}
                  {enrichmentData.education?.length > 0 && (
                    <label className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg cursor-pointer hover:bg-emerald-100 transition-colors border border-emerald-200">
                      <input
                        type="checkbox"
                        checked={selectedEnrichments.education || false}
                        onChange={(e) => setSelectedEnrichments(s => ({ ...s, education: e.target.checked }))}
                        className="mt-1 w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-emerald-900 flex items-center gap-2">
                          <span>🎓</span> Education ({enrichmentData.education.length} schools)
                        </p>
                        <div className="mt-2 space-y-1.5">
                          {enrichmentData.education.map((edu, i) => (
                            <div key={i} className="text-sm">
                              <span className="font-medium text-emerald-800">{edu.institution}</span>
                              {edu.degree && <span className="text-emerald-600"> • {edu.degree}</span>}
                              {edu.year && <span className="text-emerald-500 text-xs ml-1">({edu.year})</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    </label>
                  )}
                  
                  {/* Tags */}
                  {enrichmentData.suggested_tags?.length > 0 && (
                    <label className="flex items-start gap-3 p-3 bg-pink-50 rounded-lg cursor-pointer hover:bg-pink-100 transition-colors border border-pink-200">
                      <input
                        type="checkbox"
                        checked={selectedEnrichments.tags || false}
                        onChange={(e) => setSelectedEnrichments(s => ({ ...s, tags: e.target.checked }))}
                        className="mt-1 w-4 h-4 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-pink-900 flex items-center gap-2">
                          <span>🏷️</span> Tags ({enrichmentData.suggested_tags.length})
                        </p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {enrichmentData.suggested_tags.slice(0, 8).map((tag, i) => (
                            <span key={i} className="px-2 py-0.5 bg-pink-200 text-pink-800 text-xs rounded-full">
                              {tag}
                            </span>
                          ))}
                          {enrichmentData.suggested_tags.length > 8 && (
                            <span className="text-xs text-pink-500 self-center">+{enrichmentData.suggested_tags.length - 8} more</span>
                          )}
                        </div>
                      </div>
                    </label>
                  )}
                  
                  {/* Stats / Accomplishments */}
                  {enrichmentData.stats?.length > 0 && (
                    <label className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg cursor-pointer hover:bg-orange-100 transition-colors border border-orange-200">
                      <input
                        type="checkbox"
                        checked={selectedEnrichments.stats || false}
                        onChange={(e) => setSelectedEnrichments(s => ({ ...s, stats: e.target.checked }))}
                        className="mt-1 w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-orange-900 flex items-center gap-2">
                          <span>🏆</span> Key Accomplishments ({enrichmentData.stats.length})
                        </p>
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          {enrichmentData.stats.slice(0, 4).map((stat, i) => (
                            <div key={i} className="text-sm bg-orange-100 px-2 py-1 rounded">
                              <span className="font-semibold text-orange-800">{stat.value}</span>
                              <span className="text-orange-600 text-xs ml-1">{stat.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </label>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-3 border-t border-gray-100">
                    <Button 
                      variant="outline" 
                      onClick={() => { setEnrichmentData(null); setSelectedEnrichments({}); }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleApplyEnrichments}
                      disabled={enriching || Object.values(selectedEnrichments).every(v => !v)}
                      className="flex-1 bg-violet-600 hover:bg-violet-700 text-white"
                    >
                      {enriching ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Applying...</> : '✨ Apply Selected'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* JSON Mode */}
          {enrichMode === 'json' && (
            <div className="space-y-4 mt-4">
              <p className="text-sm text-gray-600">
                Paste JSON data to automatically populate your profile cards.
              </p>
              <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500">
                <p className="font-medium mb-2">Supported fields:</p>
                <ul className="space-y-1 ml-4 list-disc">
                  <li><strong>Investment Focus:</strong> invest_in, sectors, industries, do_not_invest_in</li>
                  <li><strong>How I Invest:</strong> lead_follow, stage, check_size, ownership_target, b2b_b2c, geo_focus</li>
                </ul>
              </div>
              <Textarea
                value={enrichJson}
                onChange={(e) => setEnrichJson(e.target.value)}
                placeholder='{"invest_in": ["SaaS", "AI/ML"], "stage": ["Seed", "Series A"]}'
                className="font-mono h-48"
              />
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => { setShowEnrichDialog(false); setEnrichJson(''); }}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleEnrichFromJson}
                  disabled={enriching || !enrichJson.trim()}
                  className="bg-violet-600 hover:bg-violet-700"
                >
                  {enriching ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing...</> : '✨ Enrich'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Apps Panel Dialog */}
      <Dialog open={showAppsPanel} onOpenChange={setShowAppsPanel}>
        <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden">
          <AppsPanel
            apps={page?.apps || []}
            onAddApp={async (appDef) => {
              // Check if app type already exists
              const existingApp = (page?.apps || []).find(a => a.type === appDef.type);
              if (existingApp) {
                toast.info('This app is already added. Click settings to edit it.');
                setEditingApp(existingApp);
                setShowAppsPanel(false);
                return;
              }
              
              try {
                const newApp = {
                  id: `app_${Date.now()}`,
                  type: appDef.type,
                  enabled: true,
                  button_text: appDef.buttonText,
                  button_icon: appDef.icon,
                  title: appDef.name,
                  description: appDef.description,
                  fields: appDef.defaultFields.map(f => ({ ...f }))
                };
                const result = await addAppToPage(page.id, newApp);
                setPage(prev => ({ ...prev, apps: result.apps }));
                toast.success(`${appDef.name} added!`);
                setShowAppsPanel(false);
                setEditingApp(newApp);
              } catch (err) {
                console.error('Failed to add app:', err);
                toast.error('Failed to add app');
              }
            }}
            onEditApp={(app) => {
              setEditingApp(app);
              setShowAppsPanel(false);
            }}
            onRemoveApp={async (app) => {
              if (!window.confirm('Remove this app?')) return;
              try {
                await deleteAppFromPage(page.id, app.id);
                setPage(prev => ({ 
                  ...prev, 
                  apps: (prev.apps || []).filter(a => a.id !== app.id) 
                }));
                toast.success('App removed');
              } catch (err) {
                console.error('Failed to remove app:', err);
                toast.error('Failed to remove app');
              }
            }}
            isFloating={false}
          />
        </DialogContent>
      </Dialog>

      {/* App Editor Dialog - Use different editors based on app type */}
      {editingApp?.type === APP_TYPES.MEETING ? (
        <MeetingAppEditor
          app={editingApp}
          isOpen={!!editingApp}
          onClose={() => setEditingApp(null)}
          pageSlug={page?.slug}
          onSave={async (updatedApp) => {
            try {
              const result = await updateAppConfig(page.id, updatedApp.id, updatedApp);
              setPage(prev => ({ ...prev, apps: result.apps }));
              setEditingApp(null);
              toast.success('App saved!');
            } catch (err) {
              console.error('Failed to save app:', err);
              toast.error('Failed to save app');
            }
          }}
        />
      ) : editingApp?.type === APP_TYPES.PITCH ? (
        /* Full-page Pitch Form Builder - rendered at root level to cover toolbar */
        <PitchFormBuilder
          app={editingApp}
          pageId={page?.id}
          onClose={() => setEditingApp(null)}
          onSave={async (updatedApp, keepOpen = false) => {
            try {
              const result = await updateAppConfig(page.id, updatedApp.id, updatedApp);
              setPage(prev => ({ ...prev, apps: result.apps }));
              // Update editingApp with new data but don't close if keepOpen
              if (keepOpen) {
                setEditingApp(updatedApp);
              } else {
                setEditingApp(null);
                toast.success('Pitch form saved!');
              }
            } catch (err) {
              console.error('Failed to save pitch form:', err);
              toast.error('Failed to save pitch form');
              throw err; // Re-throw so PitchFormBuilder can handle it
            }
          }}
        />
      ) : editingApp ? (
        <AppEditor
          app={editingApp}
          isOpen={!!editingApp}
          onClose={() => setEditingApp(null)}
          onSave={async (updatedApp) => {
            try {
              const result = await updateAppConfig(page.id, updatedApp.id, updatedApp);
              setPage(prev => ({ ...prev, apps: result.apps }));
              setEditingApp(null);
              toast.success('App saved!');
            } catch (err) {
              console.error('Failed to save app:', err);
              toast.error('Failed to save app');
            }
          }}
        />
      ) : null}

      {/* Pitch Mailbox */}
      <PitchMailbox
        pageId={page?.id}
        isOpen={showMailbox}
        onClose={() => setShowMailbox(false)}
      />

      <style>{`
        .editable-field { cursor: text; padding: 2px 4px; margin: -2px -4px; border-radius: 4px; transition: background 0.2s; }
        .editable-field:hover { background: rgba(0,0,0,0.03); }
        .editable-field:focus { background: rgba(0,0,0,0.05); outline: none; }
        .cards-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
        @media (max-width: 768px) { .cards-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
}

// ===== Dialog Components =====

function StatsEditorDialog({ open, onOpenChange, statsCard, onSave }) {
  // Use key to reset state when card changes
  const [stats, setStats] = useState(statsCard?.content?.stats || []);
  const [title, setTitle] = useState(statsCard?.title || 'at a glance');
  const [icon, setIcon] = useState(statsCard?.icon || '📊');
  const [extras, setExtras] = useState(statsCard?.extras || {});

  // Reset state when dialog opens - use useMemo-like pattern
  const cardId = statsCard?.id;
  const [lastCardId, setLastCardId] = useState(null);
  if (open && cardId !== lastCardId) {
    setLastCardId(cardId);
    setStats(statsCard?.content?.stats || []);
    setTitle(statsCard?.title || 'at a glance');
    setIcon(statsCard?.icon || '📊');
    setExtras(statsCard?.extras || {});
  }

  if (!statsCard) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit At a Glance</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="flex gap-3">
            <div className="w-20">
              <label className="text-sm font-medium text-gray-600">Icon</label>
              <Input value={icon} onChange={(e) => setIcon(e.target.value)} className="mt-1 text-center text-xl" />
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-600">Title</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" />
            </div>
          </div>
          {stats.map((stat, i) => (
            <div key={i} className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="text-sm text-gray-600">Label</label>
                <Input
                  value={stat.label}
                  onChange={(e) => {
                    const newStats = [...stats];
                    newStats[i] = { ...stat, label: e.target.value };
                    setStats(newStats);
                  }}
                  className="mt-1"
                />
              </div>
              <div className="flex-1">
                <label className="text-sm text-gray-600">Value</label>
                <Input
                  value={stat.value}
                  onChange={(e) => {
                    const newStats = [...stats];
                    newStats[i] = { ...stat, value: e.target.value };
                    setStats(newStats);
                  }}
                  className="mt-1"
                />
              </div>
              <Button variant="ghost" size="icon" onClick={() => setStats(stats.filter((_, idx) => idx !== i))}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
          <Button variant="outline" onClick={() => setStats([...stats, { label: 'new stat', value: '0' }])} className="w-full">
            <Plus className="w-4 h-4 mr-2" /> Add Stat
          </Button>
          <CardExtras extras={extras} onChange={setExtras} cardType="STATS_GRID" />
          <Button onClick={() => { onSave({ content: { stats }, title, icon, extras }); onOpenChange(false); }} className="w-full bg-[#1a1a1a]">
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HowIInvestEditorDialog({ open, onOpenChange, card, onSave }) {
  const [content, setContent] = useState(card?.content || {});
  const [title, setTitle] = useState(card?.title || 'how I invest');
  const [icon, setIcon] = useState(card?.icon || '💰');
  const [extras, setExtras] = useState(card?.extras || {});

  // Reset state when dialog opens
  const cardId = card?.id;
  const [lastCardId, setLastCardId] = useState(null);
  if (open && cardId !== lastCardId) {
    setLastCardId(cardId);
    setContent(card?.content || {});
    setTitle(card?.title || 'how I invest');
    setIcon(card?.icon || '💰');
    setExtras(card?.extras || {});
  }

  if (!card) return null;

  const toggleOption = (field, option) => {
    const current = content[field] || [];
    const updated = current.includes(option) 
      ? current.filter(x => x !== option) 
      : [...current, option];
    setContent({ ...content, [field]: updated });
  };

  const renderOptionGroup = (field, label, options) => (
    <div key={field}>
      <label className="text-sm font-medium text-gray-600">{label}</label>
      <div className="flex flex-wrap gap-2 mt-2 p-3 border rounded-md bg-gray-50">
        {options.map(opt => {
          const isSelected = (content[field] || []).includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => toggleOption(field, opt)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                isSelected ? 'bg-[#1a1a1a] text-white' : 'bg-white border text-gray-600 hover:border-gray-400'
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit How I Invest</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="flex gap-3">
            <div className="w-20">
              <label className="text-sm font-medium text-gray-600">Icon</label>
              <Input value={icon} onChange={(e) => setIcon(e.target.value)} className="mt-1 text-center text-xl" />
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-600">Title</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" />
            </div>
          </div>
          {renderOptionGroup('lead_follow', 'Lead / Follow', INVEST_OPTIONS.lead_follow)}
          {renderOptionGroup('primary_stage', 'Primary Stage', INVEST_OPTIONS.primary_stage)}
          {renderOptionGroup('check_size', 'Check Size', INVEST_OPTIONS.check_size)}
          {renderOptionGroup('ownership_target', 'Ownership Target', INVEST_OPTIONS.ownership_target)}
          {renderOptionGroup('hardware_software', 'Hardware / Software', INVEST_OPTIONS.hardware_software)}
          {renderOptionGroup('b2b_b2c', 'B2B / B2C', INVEST_OPTIONS.b2b_b2c)}
          {renderOptionGroup('geo_focus', 'Geo Focus', INVEST_OPTIONS.geo_focus)}
          <CardExtras extras={extras} onChange={setExtras} cardType="HOW_I_INVEST" />
          <Button onClick={() => { onSave({ content, title, icon, extras }); onOpenChange(false); }} className="w-full bg-[#1a1a1a]">
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InvestmentFocusEditorDialog({ open, onOpenChange, card, onSave }) {
  const [content, setContent] = useState(card?.content || { invest_in: [], do_not_invest_in: [] });
  const [title, setTitle] = useState(card?.title || 'investment focus');
  const [icon, setIcon] = useState(card?.icon || '🎯');
  const [extras, setExtras] = useState(card?.extras || {});

  // Reset state when dialog opens
  const cardId = card?.id;
  const [lastCardId, setLastCardId] = useState(null);
  if (open && cardId !== lastCardId) {
    setLastCardId(cardId);
    setContent(card?.content || { invest_in: [], do_not_invest_in: [] });
    setTitle(card?.title || 'investment focus');
    setIcon(card?.icon || '🎯');
    setExtras(card?.extras || {});
  }

  if (!card) return null;

  const toggleIndustry = (field, industry) => {
    const current = content[field] || [];
    const updated = current.includes(industry)
      ? current.filter(x => x !== industry)
      : [...current, industry];
    setContent({ ...content, [field]: updated });
  };

  const addCustomTag = (field, value) => {
    if (value && !(content[field] || []).includes(value)) {
      setContent({ ...content, [field]: [...(content[field] || []), value] });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Investment Focus</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="flex gap-3">
            <div className="w-20">
              <label className="text-sm font-medium text-gray-600">Icon</label>
              <Input value={icon} onChange={(e) => setIcon(e.target.value)} className="mt-1 text-center text-xl" />
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-600">Title</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" />
            </div>
          </div>

          {/* I Invest In */}
          <div>
            <label className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-green-500"></span>
              I Invest In
            </label>
            <div className="flex flex-wrap gap-2 mt-2 p-3 border rounded-md bg-green-50/50">
              {INVEST_OPTIONS.industries.map(opt => {
                const isSelected = (content.invest_in || []).includes(opt);
                const isInDoNot = (content.do_not_invest_in || []).includes(opt);
                return (
                  <button
                    key={opt}
                    type="button"
                    disabled={isInDoNot}
                    onClick={() => toggleIndustry('invest_in', opt)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      isInDoNot ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : isSelected ? 'bg-green-600 text-white'
                        : 'bg-white border text-gray-600 hover:border-green-400'
                    }`}
                  >
                    {opt}
                  </button>
                );
              })}
              {(content.invest_in || []).filter(t => !INVEST_OPTIONS.industries.includes(t)).map(tag => (
                <span key={tag} className="px-3 py-1.5 rounded-full text-xs font-medium bg-green-600 text-white flex items-center gap-1">
                  {tag}
                  <button type="button" onClick={() => setContent({ ...content, invest_in: content.invest_in.filter(x => x !== tag) })}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Input
                id="custom-invest-input"
                placeholder="Add custom tag..."
                className="text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCustomTag('invest_in', e.target.value.trim());
                    e.target.value = '';
                  }
                }}
              />
              <Button variant="outline" size="sm" onClick={() => {
                const input = document.getElementById('custom-invest-input');
                addCustomTag('invest_in', input?.value?.trim());
                if (input) input.value = '';
              }}>
                Add
              </Button>
            </div>
          </div>

          {/* I Don&apos;t Invest In */}
          <div>
            <label className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-red-500"></span>
              I Don&apos;t Invest In
            </label>
            <div className="flex flex-wrap gap-2 mt-2 p-3 border rounded-md bg-red-50/50">
              {INVEST_OPTIONS.industries.map(opt => {
                const isSelected = (content.do_not_invest_in || []).includes(opt);
                const isInInvest = (content.invest_in || []).includes(opt);
                return (
                  <button
                    key={opt}
                    type="button"
                    disabled={isInInvest}
                    onClick={() => toggleIndustry('do_not_invest_in', opt)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      isInInvest ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : isSelected ? 'bg-red-600 text-white'
                        : 'bg-white border text-gray-600 hover:border-red-400'
                    }`}
                  >
                    {opt}
                  </button>
                );
              })}
              {(content.do_not_invest_in || []).filter(t => !INVEST_OPTIONS.industries.includes(t)).map(tag => (
                <span key={tag} className="px-3 py-1.5 rounded-full text-xs font-medium bg-red-600 text-white flex items-center gap-1">
                  {tag}
                  <button type="button" onClick={() => setContent({ ...content, do_not_invest_in: content.do_not_invest_in.filter(x => x !== tag) })}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Input
                id="custom-donot-input"
                placeholder="Add custom tag..."
                className="text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCustomTag('do_not_invest_in', e.target.value.trim());
                    e.target.value = '';
                  }
                }}
              />
              <Button variant="outline" size="sm" onClick={() => {
                const input = document.getElementById('custom-donot-input');
                addCustomTag('do_not_invest_in', input?.value?.trim());
                if (input) input.value = '';
              }}>
                Add
              </Button>
            </div>
          </div>

          <CardExtras extras={extras} onChange={setExtras} cardType="INVESTMENT_FOCUS" />
          <Button onClick={() => { onSave({ content, title, icon, extras }); onOpenChange(false); }} className="w-full bg-[#1a1a1a]">
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PostInvestmentEditorDialog({ open, onOpenChange, card, onSave }) {
  const [supportAreas, setSupportAreas] = useState(card?.content?.support_areas || []);
  const [title, setTitle] = useState(card?.title || 'post-investment support');
  const [icon, setIcon] = useState(card?.icon || '🤝');
  const [newArea, setNewArea] = useState('');
  const [extras, setExtras] = useState(card?.extras || {});

  // Reset state when dialog opens
  const cardId = card?.id;
  const [lastCardId, setLastCardId] = useState(null);
  if (open && cardId !== lastCardId) {
    setLastCardId(cardId);
    setSupportAreas(card?.content?.support_areas || []);
    setTitle(card?.title || 'post-investment support');
    setIcon(card?.icon || '🤝');
    setExtras(card?.extras || {});
  }

  if (!card) return null;

  const addArea = () => {
    if (newArea.trim() && !supportAreas.includes(newArea.trim())) {
      setSupportAreas([...supportAreas, newArea.trim()]);
      setNewArea('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Post-Investment Support</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="flex gap-3">
            <div className="w-20">
              <label className="text-sm font-medium text-gray-600">Icon</label>
              <Input value={icon} onChange={(e) => setIcon(e.target.value)} className="mt-1 text-center text-xl" />
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-600">Title</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-600">Support Areas</label>
            <div className="flex flex-wrap gap-2 mt-2 p-3 border rounded-md bg-gray-50 min-h-[60px]">
              {supportAreas.map((area, i) => (
                <span key={i} className="px-3 py-1.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800 flex items-center gap-1">
                  {area}
                  <button type="button" onClick={() => setSupportAreas(supportAreas.filter((_, idx) => idx !== i))}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Input
                value={newArea}
                onChange={(e) => setNewArea(e.target.value)}
                placeholder="Add support area..."
                className="text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addArea();
                  }
                }}
              />
              <Button type="button" variant="outline" size="sm" onClick={addArea}>
                Add
              </Button>
            </div>
          </div>

          <CardExtras extras={extras} onChange={setExtras} cardType="POST_INVESTMENT_SUPPORT" />
          <Button type="button" onClick={() => { onSave({ content: { support_areas: supportAreas }, title, icon, extras }); onOpenChange(false); }} className="w-full bg-[#1a1a1a]">
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AnnouncementEditorDialog({ open, onOpenChange, card, onSave }) {
  const [text, setText] = useState(card?.content?.text || '');
  const [extras, setExtras] = useState(card?.extras || {});

  // Reset state when dialog opens
  const cardId = card?.id;
  const [lastCardId, setLastCardId] = useState(null);
  if (open && cardId !== lastCardId) {
    setLastCardId(cardId);
    setText(card?.content?.text || '');
    setExtras(card?.extras || {});
  }

  if (!card) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Announcement</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <label className="text-sm font-medium text-gray-600">Announcement Text</label>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Share your latest news, achievement, or update..."
              className="mt-1"
              rows={4}
            />
          </div>
          <CardExtras extras={extras} onChange={setExtras} cardType="ANNOUNCEMENT" />
          <Button onClick={() => { onSave({ content: { text }, extras }); onOpenChange(false); }} className="w-full bg-[#1a1a1a]">
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InvestmentThesisEditorDialog({ open, onOpenChange, card, onSave }) {
  const [text, setText] = useState(card?.content?.text || '');
  const [extras, setExtras] = useState(card?.extras || {});

  // Reset state when dialog opens
  const cardId = card?.id;
  const [lastCardId, setLastCardId] = useState(null);
  if (open && cardId !== lastCardId) {
    setLastCardId(cardId);
    setText(card?.content?.text || '');
    setExtras(card?.extras || {});
  }

  if (!card) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Investment Thesis</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <label className="text-sm font-medium text-gray-600">Your Investment Thesis</label>
            <p className="text-xs text-gray-500 mb-2">Describe your investment philosophy, what excites you, and how you think about investing.</p>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="I believe in backing founders who are building for the long term..."
              className="mt-1"
              rows={6}
            />
          </div>
          <CardExtras extras={extras} onChange={setExtras} cardType="INVESTMENT_THESIS" />
          <Button onClick={() => { onSave({ content: { text }, extras }); onOpenChange(false); }} className="w-full bg-[#1a1a1a]">
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ContentListItem component with podcast and newsletter autocomplete
function ContentListItem({ item, onUpdate, onDelete }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [suggestionType, setSuggestionType] = useState(null); // 'podcast' or 'newsletter'
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle name input change with debounced search for podcasts, newsletters, and books
  const handleNameChange = async (value) => {
    onUpdate({ ...item, name: value });
    
    const canSearch = (item.type === 'podcast' || item.type === 'newsletter' || item.type === 'book') && value.length >= 2;
    
    if (canSearch) {
      // Clear existing timeout
      if (searchTimeout) clearTimeout(searchTimeout);
      
      // Debounce search
      const timeout = setTimeout(async () => {
        try {
          console.log(`[ContentListItem] Searching ${item.type}s for:`, value);
          
          let result;
          if (item.type === 'podcast') {
            result = await searchPodcasts(value);
            setSuggestionType('podcast');
          } else if (item.type === 'newsletter') {
            result = await searchNewsletters(value);
            setSuggestionType('newsletter');
          } else if (item.type === 'book') {
            result = await searchBooks(value);
            setSuggestionType('book');
          }
          
          setSuggestions(result?.suggestions || []);
          setShowSuggestions(result?.suggestions?.length > 0);
        } catch (err) {
          console.error('[ContentListItem] Search error:', err);
          setSuggestions([]);
        }
      }, 300);
      
      setSearchTimeout(timeout);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion) => {
    if (suggestionType === 'podcast') {
      console.log('[ContentListItem] Selected podcast:', suggestion.podcast_name, suggestion.link);
      onUpdate({ 
        ...item, 
        name: suggestion.podcast_name,
        url: suggestion.link 
      });
    } else if (suggestionType === 'newsletter') {
      console.log('[ContentListItem] Selected newsletter:', suggestion.newsletter_name, suggestion.website);
      onUpdate({ 
        ...item, 
        name: suggestion.newsletter_name,
        url: suggestion.website 
      });
    } else if (suggestionType === 'book') {
      console.log('[ContentListItem] Selected book:', suggestion.title, suggestion.website);
      onUpdate({ 
        ...item, 
        name: suggestion.title,
        author: suggestion.author,
        url: suggestion.website 
      });
    }
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const showSearchHint = item.type === 'podcast' || item.type === 'newsletter' || item.type === 'book';
  const searchHintColor = item.type === 'podcast' ? 'text-purple-500' : item.type === 'newsletter' ? 'text-orange-500' : 'text-emerald-500';
  const placeholder = item.type === 'podcast' 
    ? 'Search tech podcasts...' 
    : item.type === 'newsletter' 
      ? 'Search tech newsletters...' 
      : item.type === 'book'
        ? 'Search tech books...'
        : 'Enter name';

  return (
    <div className="flex gap-2 items-end relative">
      <div className="w-24">
        <label className="text-sm text-gray-600">Type</label>
        <select
          value={item.type || 'book'}
          onChange={(e) => onUpdate({ ...item, type: e.target.value, url: undefined })}
          className="w-full mt-1 p-2 border rounded-md"
        >
          <option value="book">book</option>
          <option value="podcast">podcast</option>
          <option value="newsletter">newsletter</option>
          <option value="blog">blog</option>
          <option value="video">video</option>
        </select>
      </div>
      <div className="flex-1 relative">
        <label className="text-sm text-gray-600">
          Name {showSearchHint && <span className={`text-xs ${searchHintColor}`}>(type to search)</span>}
        </label>
        <Input
          ref={inputRef}
          value={item.name || ''}
          onChange={(e) => handleNameChange(e.target.value)}
          onFocus={() => showSearchHint && suggestions.length > 0 && setShowSuggestions(true)}
          className="mt-1"
          placeholder={placeholder}
        />
        
        {/* Suggestions Dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div 
            ref={suggestionsRef}
            className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto"
          >
            {suggestions.map((suggestion, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleSelectSuggestion(suggestion)}
                className="w-full px-3 py-2 text-left hover:bg-gray-100 border-b last:border-b-0 transition-colors"
              >
                {suggestionType === 'podcast' ? (
                  <>
                    <div className="font-medium text-sm">{suggestion.podcast_name}</div>
                    {suggestion.host_publisher && (
                      <div className="text-xs text-gray-500">{suggestion.host_publisher}</div>
                    )}
                    {suggestion.category && (
                      <div className="text-xs text-purple-500">{suggestion.category}</div>
                    )}
                  </>
                ) : suggestionType === 'newsletter' ? (
                  <>
                    <div className="font-medium text-sm">{suggestion.newsletter_name}</div>
                    {suggestion.author_publisher && (
                      <div className="text-xs text-gray-500">{suggestion.author_publisher}</div>
                    )}
                    {suggestion.category && (
                      <div className="text-xs text-orange-500">{suggestion.category}</div>
                    )}
                  </>
                ) : suggestionType === 'book' ? (
                  <>
                    <div className="font-medium text-sm">{suggestion.title}</div>
                    {suggestion.author && (
                      <div className="text-xs text-gray-500">by {suggestion.author}</div>
                    )}
                    <div className="flex gap-2">
                      {suggestion.category && (
                        <span className="text-xs text-emerald-500">{suggestion.category}</span>
                      )}
                      {suggestion.year && (
                        <span className="text-xs text-gray-400">({suggestion.year})</span>
                      )}
                    </div>
                  </>
                ) : null}
              </button>
            ))}
          </div>
        )}
        
        {/* Manual link input for book, podcast, newsletter */}
        {(item.type === 'book' || item.type === 'podcast' || item.type === 'newsletter') && (
          <div className="mt-2">
            <label className="text-xs text-gray-500">Link (optional)</label>
            <Input
              value={item.url || ''}
              onChange={(e) => onUpdate({ ...item, url: e.target.value || undefined })}
              className="mt-1 text-sm"
              placeholder="https://..."
            />
          </div>
        )}
      </div>
      <Button variant="ghost" size="icon" onClick={onDelete}>
        <Trash2 className="w-4 h-4 text-red-500" />
      </Button>
    </div>
  );
}

// Consistent styled input wrapper
const InputField = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</label>
    {children}
    {hint && <p className="text-xs text-gray-400">{hint}</p>}
  </div>
);

// Section header with title/icon inputs
const SectionHeader = ({ iconPlaceholder, titlePlaceholder, icon, setIcon, title, setTitle }) => (
  <div className="flex gap-3 pb-4 border-b border-gray-100">
    <div className="w-14">
      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Icon</label>
      <Input 
        value={icon} 
        onChange={(e) => setIcon(e.target.value)} 
        className="text-center text-lg h-10" 
        placeholder={iconPlaceholder} 
      />
    </div>
    <div className="flex-1">
      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Section Title</label>
      <Input 
        value={title} 
        onChange={(e) => setTitle(e.target.value)} 
        className="h-10"
        placeholder={titlePlaceholder} 
      />
    </div>
  </div>
);

function CardEditor({ card, onSave, onCancel }) {
  const [content, setContent] = useState(card.content || {});
  const [title, setTitle] = useState(card.title || '');
  const [icon, setIcon] = useState(card.icon || '');
  const [extras, setExtras] = useState(card.extras || {});

  const renderEditor = () => {
    switch (card.type) {
      case 'ADVICE':
        return (
          <div className="space-y-4">
            <SectionHeader iconPlaceholder="💡" titlePlaceholder="advice" icon={icon} setIcon={setIcon} title={title} setTitle={setTitle} />
            <InputField label="Your Advice" hint="Share wisdom or guidance for others">
              <Textarea
                value={content.text || ''}
                onChange={(e) => setContent({ ...content, text: e.target.value })}
                className="min-h-[120px] text-base"
                placeholder="Write your advice here..."
              />
            </InputField>
          </div>
        );

      case 'QUOTE':
        return (
          <div className="space-y-4">
            <SectionHeader iconPlaceholder="💬" titlePlaceholder="favorite quote" icon={icon} setIcon={setIcon} title={title} setTitle={setTitle} />
            <InputField label="Quote" hint="A quote that inspires you">
              <Textarea
                value={content.text || ''}
                onChange={(e) => setContent({ ...content, text: e.target.value })}
                className="min-h-[100px] text-base italic"
                placeholder="Enter the quote..."
              />
            </InputField>
            <InputField label="Author (Optional)">
              <Input
                value={content.author || ''}
                onChange={(e) => setContent({ ...content, author: e.target.value })}
                placeholder="Who said this?"
              />
            </InputField>
          </div>
        );

      case 'JOURNEY_TIMELINE':
        return (
          <div className="space-y-4">
            <SectionHeader iconPlaceholder="🛤️" titlePlaceholder="the journey" icon={icon} setIcon={setIcon} title={title} setTitle={setTitle} />
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Career Timeline</span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setContent({ ...content, items: [...(content.items || []), { company: '', role: '', is_current: false }] })}
                  className="text-violet-600 border-violet-200 hover:bg-violet-50"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
              
              {(!content.items || content.items.length === 0) && (
                <div 
                  onClick={() => setContent({ ...content, items: [{ company: '', role: '', is_current: true }] })}
                  className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center cursor-pointer hover:border-violet-300 hover:bg-violet-50/30 transition-all"
                >
                  <p className="text-sm text-gray-500">Add your first position</p>
                </div>
              )}
              
              {(content.items || []).map((item, i) => (
                <div key={i} className="bg-white border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 flex items-center justify-between border-b">
                    <span className="text-xs font-medium text-gray-500">Position #{i + 1}</span>
                    <button onClick={() => {
                      const items = (content.items || []).filter((_, idx) => idx !== i);
                      setContent({ ...content, items });
                    }} className="text-gray-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="p-3 space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <InputField label="Company">
                        <Input
                          value={item.company || ''}
                          onChange={(e) => {
                            const items = [...(content.items || [])];
                            items[i] = { ...item, company: e.target.value };
                            setContent({ ...content, items });
                          }}
                          placeholder="Company name"
                        />
                      </InputField>
                      <InputField label="Role">
                        <Input
                          value={item.role || ''}
                          onChange={(e) => {
                            const items = [...(content.items || [])];
                            items[i] = { ...item, role: e.target.value };
                            setContent({ ...content, items });
                          }}
                          placeholder="Your role"
                        />
                      </InputField>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={item.is_current || false}
                        onChange={(e) => {
                          const items = [...(content.items || [])];
                          items[i] = { ...item, is_current: e.target.checked };
                          setContent({ ...content, items });
                        }}
                        className="rounded border-gray-300"
                      />
                      Current position
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'CONTENT_LIST':
        return (
          <div className="space-y-4">
            <SectionHeader iconPlaceholder="📚" titlePlaceholder="what I consume" icon={icon} setIcon={setIcon} title={title} setTitle={setTitle} />
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Books, Podcasts, etc.</span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setContent({ ...content, items: [...(content.items || []), { type: 'book', name: '' }] })}
                  className="text-violet-600 border-violet-200 hover:bg-violet-50"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
              
              {(!content.items || content.items.length === 0) && (
                <div 
                  onClick={() => setContent({ ...content, items: [{ type: 'book', name: '' }] })}
                  className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center cursor-pointer hover:border-violet-300 hover:bg-violet-50/30 transition-all"
                >
                  <p className="text-sm text-gray-500">Add books, podcasts, or articles you love</p>
                </div>
              )}
              
              {(content.items || []).map((item, i) => (
                <ContentListItem
                  key={i}
                  item={item}
                  index={i}
                  onUpdate={(updatedItem) => {
                    const items = [...(content.items || [])];
                    items[i] = updatedItem;
                    setContent({ ...content, items });
                  }}
                  onDelete={() => {
                    const items = (content.items || []).filter((_, idx) => idx !== i);
                    setContent({ ...content, items });
                  }}
                />
              ))}
            </div>
          </div>
        );

      case 'OUTSIDE_OF_WORK':
        return (
          <div className="space-y-4">
            <SectionHeader iconPlaceholder="☀️" titlePlaceholder="outside of work" icon={icon} setIcon={setIcon} title={title} setTitle={setTitle} />
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Hobbies & Interests</span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setContent({ ...content, items: [...(content.items || []), { icon: '', text: '' }] })}
                  className="text-violet-600 border-violet-200 hover:bg-violet-50"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
              
              {(!content.items || content.items.length === 0) && (
                <div 
                  onClick={() => setContent({ ...content, items: [{ icon: '🎾', text: '' }] })}
                  className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center cursor-pointer hover:border-violet-300 hover:bg-violet-50/30 transition-all"
                >
                  <p className="text-sm text-gray-500">Add your hobbies and interests</p>
                </div>
              )}
              
              {(content.items || []).map((item, i) => (
                <div key={i} className="flex gap-2 items-center bg-white border rounded-lg p-2">
                  <Input
                    value={item.icon || ''}
                    onChange={(e) => {
                      const items = [...(content.items || [])];
                      items[i] = { ...item, icon: e.target.value };
                      setContent({ ...content, items });
                    }}
                    placeholder="🎾"
                    className="w-12 text-center text-lg"
                  />
                  <Input
                    value={item.text || ''}
                    onChange={(e) => {
                      const items = [...(content.items || [])];
                      items[i] = { ...item, text: e.target.value };
                      setContent({ ...content, items });
                    }}
                    placeholder="Tennis on weekends"
                    className="flex-1"
                  />
                  <button onClick={() => {
                    const items = (content.items || []).filter((_, idx) => idx !== i);
                    setContent({ ...content, items });
                  }} className="text-gray-400 hover:text-red-500 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        );

      case 'HIGHLIGHTS':
        return (
          <div className="space-y-4">
            <div className="flex gap-3">
              <div className="w-20">
                <label className="text-sm font-medium text-gray-600">Icon</label>
                <Input value={icon} onChange={(e) => setIcon(e.target.value)} className="mt-1 text-center text-xl" placeholder="⭐" />
              </div>
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-600">Title</label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" placeholder="highlights" />
              </div>
            </div>
            {(content.items || []).map((item, i) => (
              <div key={i} className="flex gap-2">
                <Input
                  value={typeof item === 'string' ? item : item.text || ''}
                  onChange={(e) => {
                    const items = [...(content.items || [])];
                    items[i] = e.target.value;
                    setContent({ ...content, items });
                  }}
                  placeholder="Achievement or highlight"
                  className="flex-1"
                />
                <Button variant="ghost" size="icon" onClick={() => {
                  const items = (content.items || []).filter((_, idx) => idx !== i);
                  setContent({ ...content, items });
                }}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
            <Button variant="outline" onClick={() => setContent({ ...content, items: [...(content.items || []), ''] })} className="w-full">
              <Plus className="w-4 h-4 mr-2" /> Add Highlight
            </Button>
          </div>
        );

      case 'EMPTY':
        return (
          <EmptyCardEditor 
            content={content} 
            setContent={setContent} 
            title={title} 
            setTitle={setTitle} 
            icon={icon} 
            setIcon={setIcon}
          />
        );

      case 'CAROUSEL':
        return <CarouselEditor content={content} setContent={setContent} title={title} setTitle={setTitle} icon={icon} setIcon={setIcon} />;

      default:
        return (
          <Textarea
            value={JSON.stringify(content, null, 2)}
            onChange={(e) => {
              try {
                setContent(JSON.parse(e.target.value));
              } catch {
                // Ignore parse errors
              }
            }}
            rows={10}
            className="font-mono"
          />
        );
    }
  };

  return (
    <div className="py-4">
      {renderEditor()}
      {/* Hide CardExtras for EMPTY cards since they have their own field system */}
      {card.type !== 'EMPTY' && (
        <CardExtras 
          extras={extras} 
          onChange={setExtras} 
          cardType={card.type}
        />
      )}
      <div className="flex justify-end gap-3 pt-4">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSave({ content, title, icon, extras })} className="bg-[#1a1a1a]">Save</Button>
      </div>
    </div>
  );
}

// Empty Card Editor Component - Flexible template with unlimited draggable fields
function EmptyCardEditor({ content, setContent, title, setTitle, icon, setIcon }) {
  const [isHorizontal, setIsHorizontal] = useState(content?.isHorizontal || false);
  const [fields, setFields] = useState(content?.fields || []);
  const [draggedField, setDraggedField] = useState(null);

  // Update content when fields change
  useEffect(() => {
    setContent({ ...content, isHorizontal, fields });
  }, [fields, isHorizontal]);

  const handleHorizontalToggle = (checked) => {
    setIsHorizontal(checked);
  };

  // Add new field
  const addField = (type) => {
    const newField = {
      id: Date.now().toString(),
      type,
      value: type === 'tags' ? [] : '',
      isBold: false,
      url: type === 'link' ? '' : undefined,
      preview: type === 'link' ? null : undefined
    };
    setFields([...fields, newField]);
  };

  // Update field
  const updateField = (id, updates) => {
    setFields(fields.map(f => f.id === id ? { ...f, ...updates } : f));
  };

  // Delete field
  const deleteField = (id) => {
    setFields(fields.filter(f => f.id !== id));
  };

  // Drag handlers
  const handleDragStart = (e, field) => {
    setDraggedField(field);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, targetField) => {
    e.preventDefault();
    if (!draggedField || draggedField.id === targetField.id) return;
    
    const dragIndex = fields.findIndex(f => f.id === draggedField.id);
    const targetIndex = fields.findIndex(f => f.id === targetField.id);
    
    if (dragIndex !== targetIndex) {
      const newFields = [...fields];
      newFields.splice(dragIndex, 1);
      newFields.splice(targetIndex, 0, draggedField);
      setFields(newFields);
    }
  };

  const handleDragEnd = () => {
    setDraggedField(null);
  };

  // Tag management
  const addTag = (fieldId, tag) => {
    const field = fields.find(f => f.id === fieldId);
    if (field && tag && !field.value.includes(tag)) {
      updateField(fieldId, { value: [...field.value, tag] });
    }
  };

  const removeTag = (fieldId, tagToRemove) => {
    const field = fields.find(f => f.id === fieldId);
    if (field) {
      updateField(fieldId, { value: field.value.filter(t => t !== tagToRemove) });
    }
  };

  return (
    <div className="space-y-5">
      {/* Header Section */}
      <div className="flex gap-4 items-start">
        <div className="w-14">
          <label className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5 block font-medium">Icon</label>
          <div className="h-11 flex items-center justify-center border border-gray-100 rounded-xl bg-gray-50/50 text-xl">
            <input 
              type="text"
              value={icon} 
              onChange={(e) => setIcon(e.target.value)} 
              className="w-full h-full text-center bg-transparent border-0 outline-none text-xl"
              placeholder="📝"
            />
          </div>
        </div>
        <div className="flex-1">
          <label className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5 block font-medium">Card Title</label>
          <input 
            type="text"
            value={title} 
            onChange={(e) => setTitle(e.target.value)} 
            placeholder="e.g., Notes, Resources, Links"
            className="w-full h-11 px-4 border border-gray-100 rounded-xl bg-gray-50/50 text-sm placeholder:text-gray-300 focus:border-violet-300 focus:ring-1 focus:ring-violet-100 outline-none transition-all"
          />
        </div>
      </div>

      {/* Featured Card Toggle */}
      <div className="flex items-center justify-between p-3 bg-gradient-to-r from-violet-50/50 to-purple-50/50 rounded-xl border border-violet-100/50">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-violet-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="7" rx="2"/>
              <rect x="3" y="14" width="18" height="7" rx="2"/>
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-800">Featured Card</p>
            <p className="text-[11px] text-gray-500">Full-width horizontal section</p>
          </div>
        </div>
        <button
          onClick={() => handleHorizontalToggle(!isHorizontal)}
          className={`relative w-11 h-6 rounded-full transition-all duration-200 ${
            isHorizontal ? 'bg-violet-600' : 'bg-gray-200'
          }`}
        >
          <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-all duration-200 ${
            isHorizontal ? 'left-6' : 'left-1'
          }`}/>
        </button>
      </div>

      {/* Fields Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Content Fields</span>
          <span className="text-[10px] text-gray-400">{fields.length} field{fields.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Fields List */}
        <div className="space-y-2">
          {fields.map((field, index) => (
            <div
              key={field.id}
              draggable
              onDragStart={(e) => handleDragStart(e, field)}
              onDragOver={(e) => handleDragOver(e, field)}
              onDragEnd={handleDragEnd}
              className={`group relative border rounded-xl bg-white transition-all ${
                draggedField?.id === field.id 
                  ? 'opacity-50 border-violet-300 shadow-lg' 
                  : 'border-gray-100 hover:border-gray-200'
              }`}
            >
              {/* Drag handle */}
              <div className="absolute left-0 top-0 bottom-0 w-8 flex items-center justify-center cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-400">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="9" cy="6" r="1.5"/>
                  <circle cx="15" cy="6" r="1.5"/>
                  <circle cx="9" cy="12" r="1.5"/>
                  <circle cx="15" cy="12" r="1.5"/>
                  <circle cx="9" cy="18" r="1.5"/>
                  <circle cx="15" cy="18" r="1.5"/>
                </svg>
              </div>

              <div className="pl-8 pr-3 py-3">
                {/* Text Field */}
                {field.type === 'text' && (
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <textarea
                        value={field.value}
                        onChange={(e) => updateField(field.id, { value: e.target.value })}
                        placeholder="Enter text..."
                        className={`w-full px-3 py-2 text-sm border border-gray-100 rounded-lg bg-gray-50/30 placeholder:text-gray-300 focus:border-violet-300 focus:bg-white outline-none resize-none transition-all ${
                          field.isBold ? 'font-semibold' : ''
                        }`}
                        rows={2}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => updateField(field.id, { isBold: !field.isBold })}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                          field.isBold 
                            ? 'bg-violet-100 text-violet-700' 
                            : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                        }`}
                        title="Toggle bold"
                      >
                        <span className="font-bold text-sm">B</span>
                      </button>
                      <button
                        onClick={() => deleteField(field.id)}
                        className="w-8 h-8 rounded-lg bg-gray-50 text-gray-400 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-all"
                        title="Delete field"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 6L6 18M6 6l12 12"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Tags Field */}
                {field.type === 'tags' && (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-1.5">
                      {field.value.map((tag, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-violet-50 text-violet-700 rounded-full text-xs font-medium"
                        >
                          {tag}
                          <button
                            onClick={() => removeTag(field.id, tag)}
                            className="hover:text-violet-900"
                          >
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M18 6L6 18M6 6l12 12"/>
                            </svg>
                          </button>
                        </span>
                      ))}
                      <input
                        type="text"
                        placeholder="Add tag..."
                        className="px-2 py-1 text-xs bg-transparent border-0 outline-none placeholder:text-gray-300 min-w-[80px]"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.target.value.trim()) {
                            addTag(field.id, e.target.value.trim());
                            e.target.value = '';
                          }
                        }}
                      />
                    </div>
                    <button
                      onClick={() => deleteField(field.id)}
                      className="text-[10px] text-gray-400 hover:text-red-500 transition-colors"
                    >
                      Remove tags field
                    </button>
                  </div>
                )}

                {/* Link Field */}
                {field.type === 'link' && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="url"
                        value={field.url || ''}
                        onChange={(e) => updateField(field.id, { url: e.target.value })}
                        placeholder="https://..."
                        className="flex-1 px-3 py-2 text-sm border border-gray-100 rounded-lg bg-gray-50/30 placeholder:text-gray-300 focus:border-violet-300 focus:bg-white outline-none transition-all"
                      />
                      <button
                        onClick={() => deleteField(field.id)}
                        className="w-9 h-9 rounded-lg bg-gray-50 text-gray-400 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-all flex-shrink-0"
                        title="Delete link"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 6L6 18M6 6l12 12"/>
                        </svg>
                      </button>
                    </div>
                    <input
                      type="text"
                      value={field.value || ''}
                      onChange={(e) => updateField(field.id, { value: e.target.value })}
                      placeholder="Link title (optional)"
                      className="w-full px-3 py-2 text-sm border border-gray-100 rounded-lg bg-gray-50/30 placeholder:text-gray-300 focus:border-violet-300 focus:bg-white outline-none transition-all"
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {fields.length === 0 && (
          <div className="py-8 text-center border-2 border-dashed border-gray-100 rounded-xl">
            <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-gray-50 flex items-center justify-center">
              <svg className="w-5 h-5 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12h14"/>
              </svg>
            </div>
            <p className="text-sm text-gray-400">Add fields below to get started</p>
          </div>
        )}

        {/* Add Field Buttons */}
        <div className="flex gap-2 pt-2">
          <button
            onClick={() => addField('text')}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-gray-200 text-gray-500 hover:border-violet-300 hover:text-violet-600 hover:bg-violet-50/30 transition-all text-sm font-medium"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 6h16M4 12h16M4 18h7"/>
            </svg>
            Text
          </button>
          <button
            onClick={() => addField('tags')}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-gray-200 text-gray-500 hover:border-violet-300 hover:text-violet-600 hover:bg-violet-50/30 transition-all text-sm font-medium"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/>
              <circle cx="7" cy="7" r="1"/>
            </svg>
            Tags
          </button>
          <button
            onClick={() => addField('link')}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-gray-200 text-gray-500 hover:border-violet-300 hover:text-violet-600 hover:bg-violet-50/30 transition-all text-sm font-medium"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
            </svg>
            Link
          </button>
        </div>
      </div>
    </div>
  );
}

// Carousel Editor Component
function CarouselEditor({ content, setContent, title, setTitle, icon, setIcon }) {
  const [unfurlLoading, setUnfurlLoading] = useState({});

  const addItem = () => {
    // Create a completely new array with the new item
    const newItem = { 
      title: '', 
      url: '', 
      image: '', 
      linkPreview: null,
      id: Date.now() // Add unique ID for key
    };
    const newItems = [...(content.items || []), newItem];
    const newContent = { ...content, items: newItems };
    console.log('[CarouselEditor] Adding item:', newItems.length, 'items now');
    setContent(newContent);
  };

  const updateItem = (index, updates) => {
    const newItems = (content.items || []).map((item, i) => 
      i === index ? { ...item, ...updates } : item
    );
    setContent({ ...content, items: newItems });
  };

  const deleteItem = (index) => {
    const newItems = (content.items || []).filter((_, i) => i !== index);
    setContent({ ...content, items: newItems });
  };

  const handleUnfurl = async (index, url) => {
    if (!url || !url.includes('.')) return;
    
    setUnfurlLoading({ ...unfurlLoading, [index]: true });
    try {
      const result = await unfurlLink(url);
      // API returns { success: true, data: { title, description, image } }
      const data = result.data || result;
      if (data.title || data.image) {
        updateItem(index, {
          url,
          title: content.items[index]?.title || data.title || '',
          image: data.image || '',
          linkPreview: {
            title: data.title || '',
            description: data.description || '',
            image: data.image || '',
            url: url
          }
        });
        toast.success('Link info fetched!');
      } else {
        toast.error('No preview available for this link');
      }
    } catch (err) {
      console.error('[CarouselEditor] Unfurl error:', err);
      toast.error('Could not fetch link info');
    } finally {
      setUnfurlLoading({ ...unfurlLoading, [index]: false });
    }
  };

  return (
    <div className="space-y-5">
      {/* Section Title - Optional */}
      <div className="flex gap-3">
        <div className="w-16">
          <label className="text-xs text-gray-500 mb-1 block">Icon</label>
          <div className="h-10 flex items-center justify-center border-2 border-gray-200 rounded-lg bg-white">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2">
              <rect x="2" y="6" width="6" height="12" rx="1"/>
              <rect x="9" y="6" width="6" height="12" rx="1"/>
              <rect x="16" y="6" width="6" height="12" rx="1"/>
            </svg>
          </div>
        </div>
        <div className="flex-1">
          <label className="text-xs text-gray-500 mb-1 block">Section Title (optional)</label>
          <Input 
            value={title} 
            onChange={(e) => setTitle(e.target.value)} 
            placeholder="e.g., Featured, Portfolio, Press" 
            className="border-2 border-gray-200 focus:border-violet-400 placeholder:text-gray-300"
          />
        </div>
      </div>

      {/* Card Size */}
      <div>
        <label className="text-xs text-gray-500 mb-2 block">Card Size</label>
        <div className="flex gap-2">
          {[
            { value: 'S', label: 'Small' },
            { value: 'M', label: 'Medium' },
            { value: 'L', label: 'Large' }
          ].map((size) => (
            <button
              key={size.value}
              onClick={() => setContent({ ...content, cardSize: size.value })}
              className={`flex-1 py-2 rounded-lg border-2 transition-all text-sm font-medium ${
                (content.cardSize || 'S') === size.value
                  ? 'border-violet-500 bg-violet-50 text-violet-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-500'
              }`}
            >
              {size.label}
            </button>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-gray-700">Carousel Items</span>
          <Button variant="outline" size="sm" onClick={addItem} className="text-violet-600 border-violet-200 hover:bg-violet-50">
            <Plus className="w-4 h-4 mr-1" /> Add Item
          </Button>
        </div>

        {/* Empty State */}
        {(!content.items || content.items.length === 0) && (
          <div 
            onClick={addItem}
            className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-violet-300 hover:bg-violet-50/30 transition-all"
          >
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Plus className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-600">Add your first carousel item</p>
            <p className="text-xs text-gray-400 mt-1">Click to add a link, image, or video</p>
          </div>
        )}

        {/* Items List */}
        {(content.items || []).map((item, i) => (
          <div key={item.id || `item-${i}`} className="border-2 border-gray-200 rounded-xl bg-white mb-3 overflow-hidden">
            {/* Item Header */}
            <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b">
              <span className="text-xs font-semibold text-gray-500">#{i + 1}</span>
              <button 
                onClick={() => deleteItem(i)}
                className="text-gray-400 hover:text-red-500 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            
            <div className="p-4 space-y-3">
              {/* Quick URL Input with Auto-fetch */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">
                  Paste a URL to auto-fill
                </label>
                <div className="flex gap-2">
                  <Input
                    value={item.url || ''}
                    onChange={(e) => updateItem(i, { url: e.target.value })}
                    placeholder="https://youtube.com/watch?v=..."
                    className="flex-1 text-sm border-2 border-gray-200 focus:border-violet-400 placeholder:text-gray-300"
                  />
                  <Button 
                    variant="default" 
                    size="sm"
                    disabled={!item.url || unfurlLoading[i]}
                    onClick={() => handleUnfurl(i, item.url)}
                    className="bg-violet-600 hover:bg-violet-700"
                  >
                    {unfurlLoading[i] ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Fetch'}
                  </Button>
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Title</label>
                <Input
                  value={item.title || ''}
                  onChange={(e) => updateItem(i, { title: e.target.value })}
                  placeholder="Enter title or fetch from URL"
                  className="text-sm border-2 border-gray-200 focus:border-violet-400 placeholder:text-gray-300"
                />
              </div>

              {/* Image URL or Preview */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Image</label>
                <Input
                  value={item.image || item.linkPreview?.image || ''}
                  onChange={(e) => updateItem(i, { image: e.target.value })}
                  placeholder="Image URL (auto-filled when you fetch)"
                  className="text-sm border-2 border-gray-200 focus:border-violet-400 placeholder:text-gray-300"
                />
              </div>

              {/* Image Preview */}
              {(item.image || item.linkPreview?.image) && (
                <div className="rounded-lg overflow-hidden border-2 border-gray-200 bg-gray-50">
                  <img 
                    src={item.image || item.linkPreview?.image} 
                    alt={item.title || 'Preview'} 
                    className="w-full h-20 object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Add More Button (when items exist) */}
        {content.items && content.items.length > 0 && (
          <button
            onClick={addItem}
            className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-500 hover:border-violet-300 hover:text-violet-600 hover:bg-violet-50/30 transition-all flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add another item
          </button>
        )}
      </div>
    </div>
  );
}
